import {
    IEditorClipart, IEditorEditorPhoto,
    IEditorElement, IEditorElementError,
    IEditorFontService,
    IEditorCanvas as ICustomizerEditorCanvas,
    IEditorImage as ICustomizerEditorImage,
    IEditorPage, IEditorPhotoGroup,
    IEditorShape,
    IEvent,
    IFreeObject,
    IMaskPreview, IMaskPreviewData,
    INonPrintableBackgroundPreview,
    IPage, IPreviewThumb, ISize, SF, GetPageErrorType,
} from "./interfaces";
import {IEditorOptions} from "./editor-interfaces";
import {RendererImage} from "./renderer-image";
import {
    CanvasMaskBWImage, CanvasNonPrintableBackgroundImage,
    EditorMaskOverlay, EditorMaskOverlayTypes, EditorNonPrintableBackground,
    IEditorPhotoElement,
    IEditorTextElement,
    IEditorText,
    IFabricOptions,
    IEditorCanvas,
    IEditorImage,
    IPageError, IWebsiteImage, IWebsiteThumb, MaskOverlayImage, NonPrintableBackgroundImage,
    IMaskPreview as IEditorMaskPreview, IKeyHole
} from "../editor/base/editor.interfaces";
import {EditorHelpers} from "@/editor/base/helpers";
import cloneDeep from "lodash.clonedeep";
import {FontService} from "@/customizer/font-service";
import {ImagingUtils} from "@/helpers/imaging-utils";
import {BackgroundElement, DefaultText, PageTypes, ProductType, ShapeAssetTypes} from "@/editor/base/models-base";
import {fabric} from "fabric";


interface IErrorAndWarnings {
        missingPhotos: number;
        errorResolution: number;
        warningResolution: number;
        missingText: number;
        warningPhotoIds: number[];
        errorPhotoIds: number[];
        photoZoneOrderWithError: number;
        photoZoneOrderWithWarning: number;
    }

    interface ILayerMargin {
        topMargin: number;
        bottomMargin: number;
        leftMargin: number;
        rightMargin: number;
    }

    type TextChangeType = 'text' | 'size' | 'color' | 'align' | 'font' | 'bold' | 'italic' | 'underline';

    enum SelectionType {
        None,
        Photo,
        Text
    }

    enum ElementResolutionError {
        None,
        Warning,
        Error
    }

    const WARNING_SEVERITY = 1;
    const ERROR_TYPE_RESOLUTION = 2;

    const BACKGROUND_TYPE = 'background';
    const EXCLUDE_LOCK_MOVEMENT_LIST: string[] = ['sf-text', 'clipart'];
    const FABRIC_EVENT_AFTER_RENDER: string = 'after:render';
    const EVENT_CANVAS_RENDER_MASK: string = 'CanvasRenderMask';
    const EVENT_CANVAS_RENDER_NON_PRINTABLE_BACKGROUND: string = 'CanvasRenderNonPrintableBackground';
    const CANVAS_BACKGROUND_COLOR_WITH_MARGIN: string = '#ffffff';

    export class EditorController {

        private readonly _readyPromise: Promise<void>;

        private fabricCanvas: IEditorCanvas | null = null;
        private scale: number = 1;

        private freeObjectIdToUid: { [key: string]: string } = {};
        private currentZone: IEditorShape | null = null;

        private _onZoneChange?: (freeObjectId: number | null, selectElement: IEditorElement | null, target: IEditorShape | null) => void;
        private _onTextDoubleClick?: () => void;
        // Currently this is all we are interested in...
        private _onZoneModified?: (freeObjectId: number, change: {
            type: 'text' | 'image',
            text?: string,
            zoom?: number,
            zone: IEditorShape
        }) => void;
        private _onTextChanged?: (freeObjectId: number, target: IEditorText) => void;
        private _onExternalLinkedZone?: (linkedZoneId: number, type: TextChangeType, value: string | number | boolean) => void;
        private _onTextOptionsChanged?: (freeObjectId: number, type: TextChangeType, value: string | number | boolean) => void;
        private _onImageDropped?: (freeObjectId: number, filename: string) => void;
        private _ready: boolean = false;

        private currentSelectionType: SelectionType = SelectionType.None;
        private canvasContainer!: HTMLDivElement;
        private overlayPosition: null | { left: number, top: number } = null;
        private size: ISize | null = null;
        private visiblePages: IEditorPage[] = []; // length > 1 only if it's a photobook with spine otherwise it should remain as a single element array
        private visiblePagesWidth?: number;
        private currentFreeObjectId: number = -1;
        private timeout?: number;

        constructor(pages: IPage[],
                    private productImageContainer: HTMLElement,
                    private fontService: IEditorFontService,
                    private options: IEditorOptions,
                    private previewImageSize: number,
                    productType: string,
                    editorType: string,
                    private isSingleImage: boolean,
                    private keyhole: IKeyHole | undefined = undefined,
                    pageLabels?: string[],
                    private maskPreviewData?: IMaskPreviewData,
                    private nonPrintableBackgroundPreviewData?: {
                        non_printable_background: {
                            L?: INonPrintableBackgroundPreview,
                            P?: INonPrintableBackgroundPreview
                        }
                    },
                    private enableCanvasMargin?: boolean) {

            this._readyPromise = new Promise<void>(async (resolve: () => void) => {
                this.visiblePages = [];
                pages.forEach((page: IPage) => {
                    const iEditorPage: IEditorPage = RendererImage.toPage(page, pageLabels, page.edits.bleed);
                    if (page.edits.bleed) {
                        //@ts-ignore
                        SF.Util.Edits.setCoverPageBleed(page.edits.bleed, iEditorPage);
                    }
                    this.visiblePages.push(iEditorPage);
                });
                this.visiblePagesWidth = undefined;
                this.sortVisiblePages();
                const width = this.getVisiblePagesWidth();
                const height = this.getVisiblePagesHeight();
                const isLandscape = width > height;

                const maxSize = isLandscape ? options.maxWidth : options.maxHeight;

                // init canvas
                // create new canvas
                const canvas = document.createElement('canvas');

                // add canvas to dom
                productImageContainer.appendChild(canvas);

                // initialize fabric object
                const fabricOptions: IFabricOptions = {renderOnAddRemove: false};
                if (options.enableMobilePointerEvents) {
                    fabricOptions.enablePointerEvents = true;
                }
                if (options.allowTouchScrolling) {
                    fabricOptions.allowTouchScrolling = true;
                }
                fabricOptions.enableCanvasMargin = this.shouldApplyMarginOnCanvas();
                // @ts-ignore
                fabricOptions.visiblePages = this.visiblePages;
                const firstPage = this.visiblePages.find((page: IEditorPage) => {
                    return page.type === PageTypes.PAGE && page.number === 1;
                });
                if (firstPage) {
                    // @ts-ignore
                    fabricOptions.firstPage = firstPage;
                }

                // @ts-ignore
                this.fabricCanvas = new SF.Editor.Canvas(canvas, fabricOptions);

                this.canvasContainer = this.productImageContainer.querySelector<HTMLDivElement>('.canvas-container') as HTMLDivElement;

                if (this.keyhole && this.hasKeyhole()) {
                    this.fabricCanvas.keyhole = this.keyhole;
                }
                // todo: Create a separate class for setting up mask/overlay in a base class that
                //  can be used by kiosk as well instead of copy/paste all over the code!
                // mask/overlay setup
                // I don't understand how maskPreviewData.mask_merge is set on back end code... I'm not sure if it works.
                // shouldn't we set mask_merge same as how we set overlay_merge on the back-end code?
                if (productType !== ProductType.ED_PASSPORT_PICTURE_PACK && maskPreviewData &&
                    (maskPreviewData.mask_bw || maskPreviewData.mask || maskPreviewData.mask_merge || maskPreviewData.overlay_merge)) {
                    this.initializeMaskData(maskPreviewData, width, height);
                    if (maskPreviewData.mask && (!this.nonPrintableBackgroundPreviewData || !this.nonPrintableBackgroundPreviewData.non_printable_background)) {
                        this.initializeBlankBackground(width, height);
                    }
                }

                if (this.nonPrintableBackgroundPreviewData && this.nonPrintableBackgroundPreviewData.non_printable_background) {
                    this.initializeNonPrintableBackgrounds(width, height);
                }

                (this.fabricCanvas as unknown as ICustomizerEditorCanvas).fontService = fontService;
                this.fabricCanvas.errorConfiguration = {
                    zoomError: options.zoomError,
                    zoomWarning: options.zoomWarning
                };

                if (options.enableFileDrag) {
                    this.setupFileDragDrop();
                }

                try {
                    if (fontService && fontService.loadFontFile) {
                        await fontService.loadFontFile();
                    } else {
                        console.error('Font service is not initialized');
                    }
                } catch (err) {
                    console.error('Error loading font file', err);
                }

                // convert sample json to page
                this.preloadFonts();
                // resizing canvas before loading edits otherwise, setCoords needs to be called on every object afterward.
                this.resizeCanvas(width, height, maxSize);
                this.loadEdits(this.visiblePages);

                if (!this.fabricCanvas) {
                    console.error('fabricCanvas is null');
                    return;
                }

                this.waitForCanvasToLoad(false).then(() => {
                    resolve();
                });

                this.fabricCanvas.controlsAboveOverlay = true;

                this.setupFabricEvents();
                this.addMaskOverlayRenderEvent();
                this.addBackgroundRenderEvent();

                if (options.enableWheel) {
                    this.enableWheel();
                }


                await this.setupControls();

            }).then(() => {
                this._ready = true;
            });
        }

        private waitForCanvasToLoad(waitForOverlayToLoad: boolean): Promise<void[]> {
            const objectToLoadPromise = new Promise<void>((resolve: () => void, reject: (error: string) => void) => {
                if (!this.fabricCanvas) {
                    reject('fabricCanvas is null');
                } else {
                    if (this.fabricCanvas.objectsToLoad === 0) {
                        resolve();
                    } else {
                        const waitForCanvas = () => {
                            if (this.fabricCanvas?.objectsToLoad === 0) {
                                resolve();
                            } else {
                                setTimeout(() => {
                                    waitForCanvas()
                                }, 100);
                            }
                        };
                        waitForCanvas();
                    }
                }
            });
            const allPromises = [objectToLoadPromise];
            if (waitForOverlayToLoad) {
                const overLayPromise = new Promise<void>((resolve: () => void, reject: (error: string) => void) => {
                    if (!this.fabricCanvas) {
                        reject('fabricCanvas is null');
                    } else {
                        const originalRender = this.fabricCanvas._renderOverlay.bind(this.fabricCanvas);

                        this.fabricCanvas._renderOverlay = (ctx: CanvasRenderingContext2D) => {
                            // Once the overlay image is actually rendered, we restore the function
                            originalRender(ctx);
                            if (this.fabricCanvas) {
                                this.fabricCanvas._renderOverlay = originalRender;
                            }
                            resolve();
                        };
                        // Force render to trigger overlay drawing
                        this.fabricCanvas.requestRenderAll();
                    }

                });
                allPromises.push(overLayPromise);
            }
            return Promise.all(allPromises);
        }

        private shouldApplyMarginOnCanvas(): boolean {
            return this.enableCanvasMargin === true && !!(this.maskPreviewData && this.maskPreviewData.mask);
        }

        private initializeMaskData(maskPreviewData: IMaskPreviewData, width: number, height: number): void {
            (this.fabricCanvas as IEditorCanvas).maskOverlayEnabled = true;

            if (maskPreviewData.mask) {
                this.renderOverlay(width, height);
            } else if (maskPreviewData.overlay_merge && Object.keys(maskPreviewData.overlay_merge).length > 0) {
                this.renderOverlay(width, height, 0);
            }

            if (maskPreviewData.mask_bw || maskPreviewData.mask_merge) {
                // TODO: Get this working with multi-page masks that are different per page. XXX: MASK_BW
                const maskRenderer = () => {
                    if (this.fabricCanvas && this.fabricCanvas.objectsToLoad === 0) {
                        if (maskPreviewData.mask_bw) {
                            if (!this.fabricCanvas.maskBW || (this.fabricCanvas.maskBW &&
                                (!this.fabricCanvas.maskBW.maskBWLandscape || !this.fabricCanvas.maskBW.maskBWPortrait))) {
                                this.setupMaskOnCanvas(width, height);
                            }
                        } else {
                            for (const p in maskPreviewData.mask_merge) {
                                if (!this.fabricCanvas.maskMerge || !this.fabricCanvas.maskMerge[p]) {
                                    this.setupMaskMergeOnCanvas(width, height, parseInt(p, 10));
                                }
                            }
                        }
                        this.drawMaskOnCanvas(width, height);
                        this.fabricCanvas.off(FABRIC_EVENT_AFTER_RENDER, maskRenderer);
                    }
                };
                (this.fabricCanvas as IEditorCanvas).on(FABRIC_EVENT_AFTER_RENDER, maskRenderer);
            }
        }

        private initializeNonPrintableBackgrounds(width: number, height: number): void {
            const fabricCanvas = this.fabricCanvas as IEditorCanvas;
            fabricCanvas.nonPrintableBackgroundEnabled = true;
            if (this.nonPrintableBackgroundPreviewData) {
                if (this.nonPrintableBackgroundPreviewData.non_printable_background) {
                    const backgroundRenderer = () => {
                        if (fabricCanvas && fabricCanvas.objectsToLoad === 0) {
                            if (!fabricCanvas.nonPrintableBackground || (fabricCanvas.nonPrintableBackgroundEnabled
                                && !fabricCanvas.nonPrintableBackground.nonPrintableBackgroundLandscape
                                && !fabricCanvas.nonPrintableBackground.nonPrintableBackgroundPortrait)) {
                                this.setupBackgroundOnCanvas(width, height);
                            }
                            this.drawBackgroundOnCanvas(width, height);
                            fabricCanvas.off(FABRIC_EVENT_AFTER_RENDER, backgroundRenderer);
                        }
                    };
                    fabricCanvas.on(FABRIC_EVENT_AFTER_RENDER, backgroundRenderer);
                }
            }
        }

        private initializeBlankBackground(width: number, height: number): void {
            const fabricCanvas = this.fabricCanvas as IEditorCanvas;
            fabricCanvas.nonPrintableBackgroundEnabled = true;
            const backgroundRenderer = () => {
                this.drawRectOnCanvas(width, height);
                fabricCanvas.off(FABRIC_EVENT_AFTER_RENDER, backgroundRenderer);
            };
            fabricCanvas.on(FABRIC_EVENT_AFTER_RENDER, backgroundRenderer);
        }

// returns a new canvas element from fabric canvas with no mask/overlay (it only includes the canvas objects)
        public toCanvasElement(outputFormat: string = ''): Promise<HTMLCanvasElement | null> {
            const originalCanvasBackgroundColor = this.fabricCanvas?.canvasBackgroundColor || '';
            if (outputFormat === 'png' && this.fabricCanvas) {
                this.fabricCanvas.canvasBackgroundColor = '';
            }
            return new Promise<HTMLCanvasElement | null>((resolve: (canvas: HTMLCanvasElement | null) => void, reject: (error: string) => void) => {
                if (this.fabricCanvas && this.fabricCanvas.wrap) {
                    const edgeStyle = this.fabricCanvas.wrap.edgeStyle;
                    this.fabricCanvas.canvasBackgroundColor = originalCanvasBackgroundColor;
                    if (edgeStyle === SF.Model.EdgeStyles.MIRROR_WRAP
                        || edgeStyle === SF.Model.EdgeStyles.BORDER
                        || edgeStyle === SF.Model.EdgeStyles.EXTEND
                        || edgeStyle === SF.Model.EdgeStyles.BORDER_WHITE
                        || edgeStyle === SF.Model.EdgeStyles.IMAGE_WRAP_WITH_BLUR
                        || edgeStyle === SF.Model.EdgeStyles.MIRROR_WRAP_WITH_BLUR) {
                        resolve(this.fabricCanvas.lowerCanvasEl);
                    } else {
                        resolve(this.fabricCanvas.toCanvasElement());
                    }
                    //   Return the lowerCanvas if there is an overlayImage, as for the preview we don't want to see the overlay
                } else if (this.fabricCanvas && this.fabricCanvas.overlayImage) {
                    this.fabricCanvas.threeDpreview = true;
                    this.fabricCanvas.renderAll();
                    this.fabricCanvas.on('after:render', (): void => {
                        if (this.fabricCanvas && this.fabricCanvas.objectsToLoad === 0) {
                            try {
                                this.fabricCanvas.off('after:render');
                                if (this.fabricCanvas.marginTop || this.fabricCanvas.marginLeft) {
                                    const width = this.getVisiblePagesWidth() * this.fabricCanvas.getZoom();
                                    const height = this.getVisiblePagesHeight() * this.fabricCanvas.getZoom();
                                    const left = this.fabricCanvas.marginLeft;
                                    const top = this.fabricCanvas.marginTop;
                                    const newCanvasEl = document.createElement('canvas');
                                    const devicePixelRatio = (fabric as any).devicePixelRatio;
                                    newCanvasEl.width = width * devicePixelRatio;
                                    newCanvasEl.height = height * devicePixelRatio;
                                    const ctx = newCanvasEl.getContext('2d');
                                    if (ctx && width > 0 && height > 0) {
                                        ctx.drawImage(this.fabricCanvas.lowerCanvasEl, left * devicePixelRatio, top * devicePixelRatio, width * devicePixelRatio, height * devicePixelRatio, 0, 0,
                                            width * devicePixelRatio, height * devicePixelRatio);
                                        this.fabricCanvas.canvasBackgroundColor = originalCanvasBackgroundColor;
                                        resolve(newCanvasEl);
                                    } else {
                                        this.fabricCanvas.canvasBackgroundColor = originalCanvasBackgroundColor;
                                        // no need to turn off after:render here. It's already turned off before if statement
                                        reject('Error creating page preview with overlay.');
                                    }

                                } else {
                                    this.fabricCanvas.canvasBackgroundColor = originalCanvasBackgroundColor;
                                    resolve(this.fabricCanvas.lowerCanvasEl);
                                }
                            } catch (error) {
                                this.fabricCanvas.canvasBackgroundColor = originalCanvasBackgroundColor;
                                this.fabricCanvas.off('after:render');
                                reject('Error creating page preview with overlay.');

                            }
                        }
                    });
                } else if (this.fabricCanvas) {
                    this.fabricCanvas.canvasBackgroundColor = originalCanvasBackgroundColor;
                    resolve(this.fabricCanvas.toCanvasElement());
                } else {
                    resolve(null);
                }
            });
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public setMaskOverlay(): void {
            if (this.fabricCanvas) {
                if (this.fabricCanvas.maskBW) {
                    this.fabricCanvas.maskOverlayEnabled = true;
                    this.fabricCanvas.renderAll();
                }
            } else {
                console.error('fabricCanvas is null');
            }
        }

        // noinspection JSUnusedGlobalSymbols - Used in customizer.ts
        public resetCanvasThreeDPreview(): void {
            if (this.fabricCanvas) {
                this.fabricCanvas.threeDpreview = false;
            }
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public setNonPrintableBackground(): void {
            if (this.fabricCanvas) {
                if (this.fabricCanvas.nonPrintableBackground) {
                    this.fabricCanvas.nonPrintableBackgroundEnabled = true;
                    this.fabricCanvas.renderAll();
                }
            } else {
                console.error('fabricCanvas is null');
            }
        }

        public addMaskOverlayRenderEvent(): void {
            document.addEventListener(EVENT_CANVAS_RENDER_MASK, () => {
                this.drawMaskOnCurrentPageSizeCanvas();
            });
        }

        public addBackgroundRenderEvent(): void {
            document.addEventListener(EVENT_CANVAS_RENDER_NON_PRINTABLE_BACKGROUND, () => {
                this.drawNonPrintableBackgroundOnCurrentPageSizeCanvas();
            });
        }

        public insertFirstPageInfo(pages: IPage[], pageLabels: string[] | undefined): void {

            const currentPages: IEditorPage[] = [];
            pages.forEach((page: IPage) => {
                currentPages.push(RendererImage.toPage(page, pageLabels));
            });
            const firstPage = currentPages.find((page: IEditorPage) => {
                return page.type === PageTypes.PAGE && page.number === 1;
            });
            if (firstPage) {
                // @ts-ignore
                (this.fabricCanvas as IEditorCanvas).firstPage = firstPage;
            }
        }

        public disposeFabricCanvas(): void {
            (this.fabricCanvas as IEditorCanvas).dispose();
        }

        public getSize(): ISize | null {
            return this.size;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public getScale(): number {
            return this.scale;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public getCanvasElement(): HTMLCanvasElement {
            return (this.fabricCanvas as IEditorCanvas).lowerCanvasEl;
        }

        public getFabricCanvas(): IEditorCanvas {
            return (this.fabricCanvas as IEditorCanvas);
        }

        public hasKeyhole(): boolean {
            return !!(this.fabricCanvas as IEditorCanvas).keyhole;
        }

        public getCurrentFreeObject(): IFreeObject | null {
            if (this.currentZone) {
                const element = (SF.Util as any).Translator.toElement(this.currentZone);
                if (element) {
                    return (SF.Util as any).Edits.fromElement(element);
                }
            }
            return null;
        }

        public setCurrentZoneByFreeObjectId(freeObjectId: number): void {
            const zone = this.getZoneByFreeObjectId(freeObjectId);
            if (zone) {
                this.setCurrentZone(zone);
            } else {
                // TODO: Consider throwing exception.
                console.error('Unable to get zone ' + freeObjectId);
            }

        }

        private getZoneByFreeObjectId(freeObjectId: number): IEditorShape | null {
            const uid = this.freeObjectIdToUid[freeObjectId];
            if (uid) {
                const canvasObjects: fabric.Object[] = (this.fabricCanvas as IEditorCanvas).getObjects();
                const currentZone = canvasObjects.find((object: fabric.Object) => (object as IEditorShape).uid === uid) as IEditorShape | null;
                if (currentZone) {
                    return currentZone;
                }
            }
            return null;
        }

        public getCurrentZone(): IEditorShape | null {
            return this.currentZone;
        }

        public setCurrentZone(zone: IEditorShape): void {
            (this.fabricCanvas as IEditorCanvas).setActiveObject(zone);
            this.objectSelected(zone, false);
            this.render();
        }

        public getFirstVisiblePageLabel(): string {
            // A bit of a hack for the photobook cover...
            if (this.visiblePages.length > 2) {
                return SF.tr('EDITOR_COVER_PAGES', 'Cover Pages', undefined, false);
            } else if (this.visiblePages.length > 0) {
                return this.visiblePages[0].pagerLabel;
            }
            return '';
        }

        public loadNewPage(pages: IPage[], pageLabels?: string[]): void {
            this.visiblePages = [];
            pages.forEach((page: IPage) => {
                const editorPage: IEditorPage = RendererImage.toPage(page, pageLabels, page.edits.bleed);
                if (page.edits.bleed) {
                    (SF.Util as any).Edits.setCoverPageBleed(page.edits.bleed, editorPage);
                }
                this.visiblePages.push(editorPage);
            });
            this.visiblePagesWidth = undefined;
            this.sortVisiblePages();

            (this.fabricCanvas as unknown as ICustomizerEditorCanvas).visiblePages = this.visiblePages;
            if (this.maskPreviewData && this.maskPreviewData.overlay_merge && Object.keys(this.maskPreviewData.overlay_merge).length > 0 && this.visiblePages.length > 0) {
                const pageIndex = this.visiblePages[0].number - 1;
                if (this.maskPreviewData.overlay_merge[pageIndex]) {
                    const width = this.getVisiblePagesWidth();
                    const height = this.getVisiblePagesHeight();
                    this.renderOverlay(width, height, pageIndex);
                } else if (this.fabricCanvas && this.fabricCanvas.overlayImage) {
                    this.fabricCanvas.overlayImage = undefined;
                }
            }
            this.insertFirstPageInfo(pages, pageLabels);
            this.loadFromPage();
        }

        public reset(pages: IEditorPage[]): void {
            this.visiblePages = cloneDeep(pages);
            this.visiblePagesWidth = undefined;
            this.loadFromPage(true);
        }

        public getEditorPage(): IEditorPage[] {
            const editorPage: IEditorPage[] = [];
            this.visiblePages.forEach((page: IEditorPage) => {
                const clonedPage = cloneDeep(page);
                editorPage.push(clonedPage);
                if (this.hasSpine()) {
                    if (page.type === PageTypes.FRONT_COVER) {
                        clonedPage.elements = this.getFrontCoverElements();
                    } else if (page.type === PageTypes.SPINE) {
                        clonedPage.elements = this.getSpineElements();
                    } else if (page.type === PageTypes.BACK_COVER) {
                        clonedPage.elements = this.getBackCoverElements();
                    }
                } else {
                    clonedPage.elements = this.getPageElements();
                }
            });

            return editorPage;
        }

        public setCurrentZoneTextOptions(type: TextChangeType,
                                         value: string | number | boolean, resetText?: boolean): IEditorText | null {
            if (this.currentZone && this.currentZone instanceof SF.Editor.Text) {

                const textZone = this.currentZone as unknown as IEditorText;
                return this.setTextZoneOptionsWithRender(textZone, type, value, resetText);
            }
            return null;
        }

        private setTextZoneOptionsWithRender(textZone: IEditorText, type: TextChangeType,
                                             value: string | number | boolean, resetText?: boolean): IEditorText {
            this.setTextZoneOptions(textZone, type, value, resetText);
            if (textZone.linkedZoneId) {
                this.updateLinkedText(textZone, type, value);
            }
            this.render();
            return textZone;
        }

        // noinspection JSUnusedGlobalSymbols
        public setTextZoneOptionsByFreeObjectId(freeObjectId: number, type: TextChangeType, value: string | number | boolean,
                                                resetText?: boolean): IEditorText | null {
            const zone = this.getZoneByFreeObjectId(freeObjectId);
            if (zone) {
                return this.setTextZoneOptionsWithRender(zone as unknown as IEditorText, type, value, resetText);
            }
            return null;
        }

        public updateLinkedText(textZone: IEditorText, type: TextChangeType,
                                value: string | number | boolean): void {
            if (type === 'size' && textZone.autoFontSize) {
                value = textZone.fontSize;
            }

            const linkedZone = (this.fabricCanvas as IEditorCanvas).getObjects().find(zone =>
                (zone as IEditorText).textZoneId === textZone.linkedZoneId) as IEditorText;
            if (linkedZone) {
                this.setTextZoneOptions(linkedZone, type, value);
                if (linkedZone.freeObjectId) {
                    if (type !== 'text') {
                        if (this._onTextOptionsChanged) {
                            this._onTextOptionsChanged(linkedZone.freeObjectId, type, value);
                        }
                    } else if (this._onTextChanged) {
                        this._onTextChanged(linkedZone.freeObjectId, linkedZone);
                    }

                }
            } else if (textZone.linkedZoneId) {
                if (this._onExternalLinkedZone) {
                    this._onExternalLinkedZone(textZone.linkedZoneId, type, value);
                }
            }
        }

        private setTextZoneOptions(textZone: IEditorText, type: TextChangeType,
                                   value: string | number | boolean, resetText?: boolean): void {
            switch (type) {
                case 'font':
                    textZone.setStyle('fontFamily', value as string);
                    break;
                case 'bold':
                    const isBold = textZone.getStyle('fontWeight') === 'bold';
                    textZone.setStyle('fontWeight', isBold ? '' : 'bold');
                    break;
                case 'italic':
                    const isItalic = textZone.getStyle('fontStyle') === 'italic';
                    textZone.setStyle('fontStyle', isItalic ? '' : 'italic');
                    break;
                case 'underline':
                    /*fabric cache should be cleared before applying underline otherwise it uses the character width in its cache which may not be correct*/
                    // @ts-ignore
                    fabric.util.clearFabricFontCache((this.currentZone as IEditorText).fontFamily);
                    textZone.setStyle('underline', !textZone.getStyle('underline'));
                    break;
                case 'text':
                    textZone.exitEditing();
                    textZone.edited = !resetText;
                    // Remove non-ascii characters, note: this will not allow non-latin characters and may have to be made more robust in the future.
                    textZone.text = textZone.fixTextFromOptions(value as string);
                    if (textZone.autoFontSize) {
                        if (textZone.autoFontSizeAdjust() && this._onTextOptionsChanged && textZone.freeObjectId) {
                            this._onTextOptionsChanged(textZone.freeObjectId, 'size', textZone.fontSize);
                            if (textZone.linkedZoneId) {
                                this.updateLinkedText(textZone, 'size', textZone.fontSize);
                            }
                        }
                    }
                    break;
                case 'size':
                    const fontSize = value as number;
                    // For some reason if autoFontSize is on, setStyle needs to be called rather than setFontSize.
                    if (textZone.autoFontSize) {
                        const doAutoAdjust = fontSize > textZone.fontSize;
                        const setFontSize = fontSize;
                        textZone.setStyle('fontSize', fontSize);
                        if (doAutoAdjust) {
                            if (textZone.autoFontSizeAdjust() && this._onTextOptionsChanged && textZone.freeObjectId) {
                                this._onTextOptionsChanged(textZone.freeObjectId, 'size', textZone.fontSize);
                            }
                        }
                        if (setFontSize === textZone.fontSize) {
                            textZone.originalFontSize = setFontSize;
                        }
                    } else {
                        textZone.fontSize = fontSize;
                    }
                    /**
                     * Regarding PS-61340: There are two initDimensions functions in fabricjs.
                     * One only updates the height of the text, while the other updates both width and height. However, the one that updates
                     * both is never called upon calling setFontSize (don't know if it's a bug, or they intentionally do not update width).
                     * We can copy the initDimensions function from fabricJs and calls it ourselves here to update both width and height. But
                     * the issue is that the text box width is going to fit the exact text width value upon the font size change, resulting
                     * in constant text wrapping whenever user types a new word.
                     */
                    break;
                case 'color':
                    textZone.setStyle('fill', value as string);
                    break;
                case 'align':
                    textZone.setStyle('textAlign', value);
                    break;
                default:
                    console.warn('Unknown type: ' + type);
            }
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public currentZoneToggleBold(): boolean {
            if (this.currentZone) {
                const isBold = this.currentZone.getStyle('fontWeight') === 'bold';
                this.setCurrentZoneTextOptions('bold', !isBold);
                return !isBold;
            } else {
                return false;
            }
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public currentZoneToggleItalic(): boolean {
            if (this.currentZone) {
                const isItalic = this.currentZone.getStyle('fontStyle') === 'italic';
                this.setCurrentZoneTextOptions('italic', !isItalic);
                return !isItalic;
            } else {
                return false;
            }
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public currentZoneToggleUnderline(): boolean {
            if (this.currentZone) {
                /*fabric cache should be cleared before applying underline otherwise it uses the character width in its cache which may not be correct*/
                // @ts-ignore
                fabric.util.clearFabricFontCache((this.currentZone as IEditorText).fontFamily);
                const hasUnderline = this.currentZone.getStyle('underline');
                this.setCurrentZoneTextOptions('underline', !hasUnderline);
                return !hasUnderline;
            } else {
                return false;
            }
        }

        public onZoneChange(onZoneChange: (freeObjectId: number | null, selectElement: IEditorElement | null, target: IEditorShape | null) => void): void {
            this._onZoneChange = onZoneChange;
        }

        public onTextDoubleClick(showEditText: () => void): void {
            this._onTextDoubleClick = showEditText;
        }

        public onZoneModified(onZoneModified: (freeObjectId: number, change: {
            type: 'text' | 'image',
            text?: string,
            zoom?: number,
            zone: IEditorShape
        }) => void): void {
            this._onZoneModified = onZoneModified;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public onTextChanged(onTextChanged: (freeObjectId: number, target: IEditorText) => void): void {
            this._onTextChanged = onTextChanged;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public onExternalLinkedZone(onExternalLinkedZone: (linkedZoneId: number, type: TextChangeType, value: string | number | boolean) => void): void {
            this._onExternalLinkedZone = onExternalLinkedZone;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public onTextOptionsChanged(onTextOptionsChanged: (freeObjectId: number, type: TextChangeType, value: string | number | boolean) => void): void {
            this._onTextOptionsChanged = onTextOptionsChanged;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public onImageDropped(onImageDropped: (freeObjectId: number, filename: string) => void): void {
            this._onImageDropped = onImageDropped;
        }

        public ready(): boolean {
            return this._ready;
        }

        public readyPromise(): Promise<void> {
            return this._readyPromise;
        }

        public hasEmptyPhoto(): boolean {
            return (this.fabricCanvas as IEditorCanvas).getObjects('photo-group').some(object => !(object as IEditorShape).photoObject.image);
        }

        public getPageIdByPointerLocation(x: number, y: number): number {
            let offset = 0;
            if (x < 0 || y < 0 || x > this.getVisiblePagesWidth() || y > this.getVisiblePagesHeight()) {
                return -1;
            }
            for (let i = 0; i < this.visiblePages.length; i++) {
                if (x > offset) {
                    // if we are an exact match, it's done
                    if (x <= offset + this.visiblePages[i].width) {
                        return this.visiblePages[i].id || 0;
                    }
                }
                offset += this.visiblePages[i].width;
            }

            return -1;
        }


        public setCurrentZoneImage(image: ICustomizerEditorImage): void {
            if (this.currentZone) {
                let innerObject = this.currentZone;

                // noinspection SuspiciousTypeOfGuard
                if (innerObject instanceof SF.Editor.PhotoGroup) {
                    innerObject = (innerObject as IEditorPhotoGroup).photoObject;
                }

                const shape = this.currentZone;
                if (shape.effects.filter && shape.effects.filter.length > 0) {
                    shape.effects.filter = '';
                }

                // if we are dropping an Edge do not reset the photo
                if (innerObject) {
                    innerObject.replaceImage(image, true, (): void => {
                        const effects = shape.effects;
                        if (image.edits) {
                            const rotation: number = parseInt(image.edits.rotation as any, 10) || 0;
                            effects.initialRotation = rotation;
                            shape.photoObject.angle = rotation;
                        }
                        if (this.currentZone) {
                            this.currentZone.uniquePhotoId = image.id || 0;
                            shape.photoObject.placePhoto();
                            if (this._onZoneModified) {
                                this._onZoneModified(shape.freeObjectId || 0, {
                                    type: 'image',
                                    zoom: shape.getZoom(),
                                    zone: this.currentZone
                                });
                            }
                        } else {
                            console.error('Current zone not found');
                        }
                        this.render();
                    });
                }
            } else {
                console.warn('No current zone');
            }

        }

        public getContainer(): HTMLElement {
            return this.fabricCanvas!.wrapperEl;
        }

        // TODO: Rename to getPages as this returns pages!
        public getPage(includePreview: boolean, projectId?: number, getTextScreenshot: boolean = false, previewOutputFormat: string = 'jpg'): Promise<IPage[]> {
            return new Promise<IPage[]>(async (resolve: (page: IPage[]) => void, reject: (err: {message: string, type: GetPageErrorType}) => void): Promise<void> => {
                let previewImage: string;

                // Because we now have to wait for all the previews, we must preload them here
                // before we clone them below otherwise we won't preview images...
                if (getTextScreenshot) {
                    const allObjects: IEditorShape[] = (this.fabricCanvas?.getObjects() || []) as unknown as IEditorShape[];
                    for (let i = 0; i < this.visiblePages.length; i++) {
                        const visiblePage: IEditorPage = this.visiblePages[i];
                        for (let j = 0; j < visiblePage.elements.length; j++) {
                            const possibleTextElement = visiblePage.elements[j];
                            if (possibleTextElement instanceof SF.Model.TextElement) {
                                const textObject = allObjects.find(obj => obj.uid === possibleTextElement.uid);
                                if (textObject) {
                                    try {
                                        const tmp = await (SF.Util as any).Translator.getPreviewImageFromTextAsBase64(textObject);
                                        // @ts-ignore
                                        (visiblePage.elements[j] as IEditorTextElement).preview_image = tmp;
                                        textObject.preview_image = tmp;
                                    } catch (err) {
                                        reject({message: 'Error creating text preview', type: 'textPreview' });
                                    }
                                }
                            }
                        }
                    }
                }
                if (includePreview) {
                    try {
                        previewImage = await this.getPreviewImage(previewOutputFormat);
                    } catch (err) {
                        reject({message: err, type: 'others'});
                    }
                } else {
                    previewImage = '';
                }

                const pages: IPage[] = [];
                this.visiblePages.forEach((visiblePage: IEditorPage) => {
                    // do not revert bleed on visible pages...
                    const page = cloneDeep(visiblePage);
                    // @ts-ignore
                    EditorHelpers.revertCoverPageBleed(page);

                    pages.push({
                        // @ts-ignore
                        edits: {
                            canDelete: page.canDelete,
                            canEdit: page.canEdit,
                            canReorder: page.canReorder,
                            height: page.height,
                            width: page.width,
                            joinedPage: page.joined,
                            pageName: page.name,
                            type: page.type,
                            freeObject: this.getFreeObjects(page),
                            mode: page.mode,
                            spanningPage: page.spanning,
                            bleed: page.bleed,
                            productType: page.productType,
                        },
                        page_id: page.id || 0,
                        page_number: page.number,
                        project_id: projectId || 0,
                        alignment: 0,
                        preview_image: previewImage
                    });
                });
                resolve(pages);
            });
        }

        public getFreeObjects(page: IEditorPage): IFreeObject[] {
            let freeObjects: IFreeObject[];
            if (this.hasSpine()) {
                freeObjects = this.getCoverPagesFreeObjects(page.type);
            } else {
                freeObjects = this.getPageElements().map(element => (SF.Util as any).Edits.fromElement(element));
            }

            return freeObjects;
        }

        public clearSelection(): void {
            (this.fabricCanvas as IEditorCanvas).discardActiveObject();
            this.render();
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public exitTextEditing(skipForceEmptyTextToDefault: boolean = false, getTextScreenshot: boolean = false): void {
            try {
                (SF.Util as any).Translator.setSaveTextPreview(getTextScreenshot);
                (this.fabricCanvas as IEditorCanvas).getObjects().forEach((object: fabric.Object) => {
                    if (object.type === 'sf-text') {
                        (object as IEditorText).exitEditing(skipForceEmptyTextToDefault);
                    }
                });
                (this.fabricCanvas as IEditorCanvas).renderAll();
                (SF.Util as any).Translator.resetSaveTextPreview();
            } catch (error) {
                (SF.Util as any).Translator.resetSaveTextPreview();
                throw new Error(error);
            }
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public removeEmptyTextZones(): number {
            const emptyTexts = (this.fabricCanvas as IEditorCanvas).getObjects().filter((object: fabric.Object) => {
                return (object.type === 'sf-text' && (object as IEditorText).text === '');
            });
            if (emptyTexts.length > 0) {
                for (let i = 0; i < emptyTexts.length; i++) {
                    (this.fabricCanvas as IEditorCanvas).remove(emptyTexts[i]);
                }
                (this.fabricCanvas as IEditorCanvas).renderAll();
                return emptyTexts.length;
            }
            return 0;
        }

        public removeCurrentZoneBorder(): void {
            if (this.currentZone && (this.currentZone as any).removeBorder) {
                (this.currentZone as any).removeBorder();
            }
        }

        public applyCurrentZoneBorder(color: string, size: number): void {
            if (this.currentZone && (this.currentZone as any).applyBorder) {
                this.currentZone.effects.borderSize = size;
                this.currentZone.effects.borderColor = color;
                (this.currentZone as any).applyBorder();
            }
        }

        public setCurrentZoneZoom(zoom: number, previousZoom: number): void {
            if (this.currentZone instanceof SF.Editor.PhotoGroup) {
                const photoObject: IEditorEditorPhoto = (this.currentZone as IEditorPhotoGroup).photoObject;
                const zoomBeforeChange = photoObject.imagePosition.zoom;
                const zoomDelta = zoom - zoomBeforeChange;
                (this.currentZone as IEditorPhotoGroup).zoom(zoomDelta);
                (photoObject.group as any).userZoom = photoObject.imagePosition.zoom;
                photoObject.elementAdded = false;
            } else if (this.currentZone as any instanceof SF.Editor.Clipart) {
                // TODO: Handle other zooms...

                const sizeZoom = !previousZoom ? 1 : zoom / previousZoom;
                const clipArt = this.currentZone as IEditorClipart;
                clipArt.setWidth(clipArt.getWidth() * sizeZoom);
                clipArt.setHeight(clipArt.getHeight() * sizeZoom);
                clipArt.currentZoom = zoom;
                clipArt.setCoords();
            }
            this.render();
        }

        public setCurrentZoneRotation(rotation: number): void {
            if (this.currentZone) {
                if (this.currentZone.type === 'sf-text') {
                    this.currentZone.rotate(rotation);
                } else if (this.currentZone.type === 'clipart') {
                    this.currentZone.angle = rotation;
                } else {
                    this.currentZone.innerRotate(rotation);
                }

                this.render();
            } else {
                console.warn('No current zone');
            }
        }

        public setFlip(flipHorizontal: boolean, flipVertical: boolean): void {
            if (this.currentZone) {
                if (this.currentZone instanceof SF.Editor.PhotoGroup) {
                    const photoObject: IEditorEditorPhoto = (this.currentZone as IEditorPhotoGroup).photoObject;
                    photoObject.flipX = flipHorizontal;
                    photoObject.flipY = flipVertical;
                } else {
                    this.currentZone.flipX = flipHorizontal;
                    this.currentZone.flipY = flipVertical;
                }
                this.render();
            } else {
                console.warn('No current zone');
            }
        }

        public currentZoneHasPhoto(): boolean {
            if (this.currentZone instanceof SF.Editor.PhotoGroup) {
                const photoObject: IEditorEditorPhoto = (this.currentZone as IEditorPhotoGroup).photoObject;
                return !!photoObject;
            } else {
                return false;
            }
        }

        public removeCurrentZone(): void {
            if (this.currentZone) {
                (this.fabricCanvas as IEditorCanvas).remove(this.currentZone);
            }
            (this.fabricCanvas as IEditorCanvas).discardActiveObject();
            this.render();
        }

        public removeCurrentZonePhoto(): void {
            if (this.currentZone instanceof SF.Editor.PhotoGroup) {
                (this.currentZone as IEditorPhotoGroup).removePhoto();
                this.render();
            }
        }

        public addText(textValue: string): void {

            const textObjects = (this.fabricCanvas as IEditorCanvas).getObjects('sf-text');
            let fontSize: number;
            let fontFamily: string;
            let fill: string;
            const width = this.getVisiblePagesWidth() * .8;
            if (textObjects.length > 0) {
                const textObject = textObjects[0] as IEditorText;
                fontSize = textObject.fontSize;
                fontFamily = textObject.fontFamily;
                fill = textObject.fill as string;
            } else {
                // taken from calculateFontSize in boardController
                const value = Math.round((Math.max(width, this.getVisiblePagesHeight()) / 5) * 0.07);
                fontSize = EditorHelpers.clamp(6, 2 * Math.round(value / 2), 60);
                fontFamily = FontService.DEFAULT_FONT;
                fill = '#000000'; // TODO: Determine color in area and get the fill from there...
            }

            const top = (this.getVisiblePagesHeight() - fontSize * 5) / 2;
            const left = this.getVisiblePagesWidth() / 2;

            // The replacement is to remove non-ascii characters, note: this will not allow non-latin characters and may have to be made more robust in the future.
            const text = new SF.Editor.Text(textValue.replace(/[^\x00-\xff]/g, ''), {
                width,
                left,
                top,
                fontSize,
                fontFamily,
                fill
            });
            this.activateDoubleClickOnText(text as unknown as IEditorText);
            text.hasControls = true;
            text.edited = true;
            text.setControlsVisibility({mtr: false});
            text.addedByUser = true;

            (this.fabricCanvas as IEditorCanvas).add(text);
            this.render();

            if (textObjects.length === 0) {
                text.enterEditing();
                const color = text.findBackgroundColor();
                const brightnessValue = 123;
                fill = EditorHelpers.isBrighterThan(color.r, color.g, color.b, brightnessValue) ? '#000000' : '#ffffff';
                (text as any).set('fill', fill);
                this.render();
            }

            setTimeout(() => {
                (this.fabricCanvas as IEditorCanvas).setActiveObject(text);
            }, 50);
        }

        public isErrorSeverity(error: IEditorElementError): boolean {
            return error.extra.severity === ERROR_TYPE_RESOLUTION;
        }

        public isWarningSeverity(error: IEditorElementError): boolean {
            return error.extra.severity === WARNING_SEVERITY;
        }

        public isResolutionTypeError(error: IEditorElementError): boolean {
            return error.type === ERROR_TYPE_RESOLUTION;
        }

        public getErrorsAndWarnings(cartItemId: number, page: IPage | undefined = undefined, ignoreResolutionWarning: boolean = false): IPageError {
            const res: IPageError = {
                warningResolution: 0,
                warningMissingText: 0,
                errorResolution: 0,
                errorMissingPhotos: 0,
                errors: [],
                photoZoneOrderWithWarning: 0,
                photoZoneOrderWithError: 0,
                cartItemId: cartItemId
            };
            if ((page && !page.edits.canEdit) || (!page && !this.canEditVisiblePages())) {
                return res;
            }
            let elements: IEditorElement[] = [];
            let elementPage: IEditorPage | undefined;
            let isCoverPage: boolean = false;
            if (page) {
                isCoverPage = [PageTypes.BACK_COVER, PageTypes.FRONT_COVER, PageTypes.SPINE].indexOf(page.edits.type) >= 0;
                if (isCoverPage && this.hasSpine()) {
                    const allElements = this.getPageElements();
                    allElements.forEach((element: IEditorElement) => {
                        const elementPage1 = this.getElementPage(element);
                        if (elementPage1 && elementPage1.type === page.edits.type) {
                            elements.push(element);
                        }
                    });
                    elementPage = this.visiblePages.find((currentPage: IEditorPage) => {
                        return currentPage.type === page.edits.type;
                    });
                } else {
                    elementPage = RendererImage.toPage(page, undefined);
                    elements = elementPage.elements;
                }
            } else {
                elements = this.getPageElements();
                elementPage = this.visiblePages[0];
            }

            /* Remove this, it breaks editing designs without photos */

            // const photoElementsOrBackgrounds = elements.filter((element: IEditorElement) => {
            //     // this is written by assuming that there should be always either a photo element or at least a background element on any product either as a photo or as a clipart
            //     if (element instanceof SF.Model.ClipartElement) {
            //         const backgroundWidth = (this.fabricCanvas as IEditorCanvas).width / (this.fabricCanvas as IEditorCanvas).getZoom();
            //         const backgroundHeight = (this.fabricCanvas as IEditorCanvas).height / (this.fabricCanvas as IEditorCanvas).getZoom();
            //         // if clipart is set as the background
            //         // multiply by .99 to address rounding issues
            //         return element.width >= backgroundWidth * .99 && element.height >= backgroundHeight * .99;
            //     }
            //     return element instanceof SF.Model.PhotoElement || element instanceof SF.Model.BackgroundElement;
            // });
            //  if ((elements.length == 0 || photoElementsOrBackgrounds.length === 0) && this.visiblePages.length == 1 && this.visiblePages[0].type == 'page' && !isCoverPage) {
            //                 res.errorMissingPhotos = 1;

            // loop over each freeObject make sure that if it's an image, then it is not missing, and it doesn't have a resolution warning
            // additionally it also ensures text is not the default.
            const info: IErrorAndWarnings = elements.reduce((accum: IErrorAndWarnings, element, index): IErrorAndWarnings => {

                // un-editable elements cannot have errors
                if (element.editable) {
                    // if we have a photo or a background
                    if (element instanceof SF.Model.PhotoElement || element instanceof BackgroundElement) {

                        // if it doesn't have an image, mark as such
                        if (!(element as unknown as IEditorPhotoElement).image) {
                            accum.missingPhotos++;
                        } else {
                            // it has an image, does it have a resolution warning?
                            const warning = this.elementHasResolutionWarning(element as unknown as IEditorPhotoElement);
                            const imageId: number = (element.image as IEditorImage).id || 0;
                            if (warning === ElementResolutionError.Error) {
                                accum.errorResolution++;
                                accum.errorPhotoIds.push(imageId);
                                accum.photoZoneOrderWithError = index;
                            } else if (warning === ElementResolutionError.Warning) {
                                accum.warningResolution++;
                                accum.warningPhotoIds.push(imageId);
                                accum.photoZoneOrderWithWarning = index;
                            }
                        }
                    }

                    // if we have a text object
                    if (element instanceof SF.Model.TextElement && this.isAnUneditedText(element as unknown as IEditorTextElement)) {
                        accum.missingText++;
                    }
                }
                return accum;
            }, {
                missingPhotos: 0,
                errorResolution: 0,
                warningResolution: 0,
                missingText: 0,
                warningPhotoIds: [],
                errorPhotoIds: [],
                photoZoneOrderWithError: -1,
                photoZoneOrderWithWarning: -1
            });

            if (info.missingPhotos > 0) {
                res.errorMissingPhotos = info.missingPhotos;

            }
            if (info.errorResolution > 0 && elementPage) {
                res.photoZoneOrderWithError = info.photoZoneOrderWithError;
                res.errorResolution = info.errorResolution;
                info.errorPhotoIds.forEach(id => {
                    res.errors.push({
                        type: ERROR_TYPE_RESOLUTION,
                        page: (elementPage as IEditorPage).index,
                        label: (elementPage as IEditorPage).getLabel(),
                        extra: {
                            severity: ERROR_TYPE_RESOLUTION,
                            photoId: id
                        }
                    });
                });
            }
            if (info.missingText) {
                res.warningMissingText = info.missingText;
            }
            if (info.warningResolution && !ignoreResolutionWarning && elementPage) {
                res.photoZoneOrderWithWarning = info.photoZoneOrderWithWarning;
                res.warningResolution = info.warningResolution;
                info.warningPhotoIds.forEach(id => {
                    res.errors.push({
                        type: ERROR_TYPE_RESOLUTION,
                        page: (elementPage as IEditorPage).index,
                        label: (elementPage as IEditorPage).getLabel(),
                        extra: {
                            severity: WARNING_SEVERITY,
                            photoId: id
                        }
                    });
                });
            }
            // } // End Commented out code

            return res;
        }

        public getCurrentZoneText(): string {
            return (this.currentZone as unknown as IEditorText).edited && (this.currentZone && (this.currentZone as unknown as IEditorText).text) ? (this.currentZone as unknown as IEditorText).text : '';
        }

        public selectObjectByIndex(objectIndex: number): void {
            const result = (this.fabricCanvas as IEditorCanvas).getObjects().find((_, index: number) => {
                return objectIndex === index;
            });
            if (result) {
                this.setCurrentZone(result as IEditorShape);
            }
        }

        public getPageElements(): IEditorElement[] {
            return (this.fabricCanvas as IEditorCanvas).getObjects()
                .map(shape => (SF.Util as any).Translator.toElement(shape as IEditorShape))
                .filter(element => !!element) as IEditorElement[];
        }

        public modifyEventsAndControlsOnCanvasObjects(lockMovement: boolean, objectList: Object[] = []): void {
            const objects = objectList.length > 0 ? objectList : this.fabricCanvas ? this.fabricCanvas.getObjects() : [];
            objects.forEach((object: any) => {
                if (EXCLUDE_LOCK_MOVEMENT_LIST.indexOf(object.type) > -1 && !object.locked) {
                    object.hasControls = true;
                    object.setControlsVisibility({mtr: false});
                } else {
                    object.hasControls = (this.options.drawControlsOnPhoto && object.type === 'photo-group') ||
                        (object.type === 'background' && this.options.backgroundHasDragControl);
                }
                if (lockMovement && EXCLUDE_LOCK_MOVEMENT_LIST.indexOf(object.type) == -1) {
                    object.lockMovementX = true;
                    object.lockMovementY = true;
                }
                if (object.type == 'sf-text') {
                    object.hasControls = this.options.textHasControls;
                    this.activateDoubleClickOnText(object);
                }

            });
        }

        public targetExists(e: Event): boolean {
            return (this.fabricCanvas as IEditorCanvas).pointerIsOnCanvasArea(e);
        }

        public getSnapShotsFromAllPages(pages: IPage[], previewerContainerWidth: number, threeDPreview: boolean = false,
                                        showDefaultText: boolean = false, outputFormat: string = 'jpg'): Promise<string[]> {
            return new Promise((resolve: (allSnapShotsBase64: string[]) => void, reject: (err: string) => void): void => {
                try {
                    const renderer = new (SF.Util as any).Renderer({fontService: this.fontService});
                    const iEditorPages: IEditorPage[] = [];
                    pages.forEach((page: IPage) => {
                        iEditorPages.push(RendererImage.toPage(page, undefined));
                    });
                    renderer.outputFormat = outputFormat;
                    renderer.render(new SF.Model.ElementCollection(iEditorPages), previewerContainerWidth, (snaps: HTMLCanvasElement[]): void => {
                        const allSnaps: string[] = [];
                        snaps.forEach((snap: HTMLCanvasElement) => {
                            allSnaps.push(this.getBase64ImageFromDataUrl(snap.toDataURL(outputFormat, 85)));
                        });
                        resolve(allSnaps);
                    }, threeDPreview, showDefaultText);
                } catch (e) {
                    reject(e);
                }
            });
        }

        public removePatternImage(freeObject: IFreeObject) {
            if (freeObject.pattern) {
                delete freeObject.pattern.patternImage;
                delete freeObject.pattern.tileHeight;
                delete freeObject.pattern.tileWidth;
            }
        }

        private sortVisiblePages(): void {
            if (this.visiblePages.length > 1) {
                this.visiblePages.sort((a: IEditorPage, b: IEditorPage) => {
                    if (a.type === PageTypes.FRONT_COVER
                        || (a.type === PageTypes.SPINE && b.type === PageTypes.BACK_COVER)) {
                        return 1;
                    } else if (a.type === PageTypes.BACK_COVER
                        || (a.type === PageTypes.SPINE && b.type === PageTypes.FRONT_COVER)) {
                        return -1;
                    }
                    return 0;
                });
            }
        }

        public getVisiblePagesWidth(): number {
            if (!this.visiblePagesWidth) {
                let pageWidth = 0;
                this.visiblePages.forEach((page: IEditorPage) => {
                    pageWidth += page.width;
                });
                this.visiblePagesWidth = pageWidth;
            }
            return this.visiblePagesWidth;
        }

        public getVisiblePagesHeight(): number {
            return this.visiblePages[0].height;
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public captureDefaultTextPreview(page: IPage): Promise<void> {

            return new Promise<void>((resolve: () => void, reject: (error: string) => void) => {
                const canvas = document.createElement('canvas');
                const fabricOptions: IFabricOptions = {renderOnAddRemove: false};
                const fabricCanvas = new SF.Editor.Canvas(canvas, fabricOptions);
                const width = page.edits.width;
                const height = page.edits.height;
                const isLandscape = width > height;
                const maxSize = isLandscape ? this.options.maxWidth : this.options.maxHeight;
                const size = EditorHelpers.getImageSizeFromMax(width, height, maxSize, this.options.maxWidth, this.options.maxHeight);
                // noinspection JSSuspiciousNameCombination
                fabricCanvas.setDimensions({
                    width: Math.floor(size.width),
                    height: Math.floor(size.height)
                });
                const scale = EditorHelpers.zoomToFit(width, height, this.canvasContainer.offsetWidth, this.canvasContainer.offsetHeight);
                fabricCanvas.setZoom(scale);
                const unEditedTexts = page.edits.freeObject.filter(freeObject => {
                    return freeObject.textEdits && (!freeObject.textEdits.edited || !freeObject.textEdits.preview_image);
                });
                if (unEditedTexts.length > 0) {
                    fabricCanvas.fontService = (this.fabricCanvas as IEditorCanvas).fontService;

                    const unEditedTextPromises = unEditedTexts.map((defaultText: IFreeObject): Promise<void> => {
                        return new Promise((textPromise, textReject) => {

                            const defaultTextElement: IEditorElement | null = (SF.Util as any).Edits.toElement(defaultText);
                            if (!defaultTextElement) {
                                textReject('Unable to create element from defaultText');
                                return;
                            }
                            const defaultTextShape: IEditorShape = (SF.Util as any).Translator.toShape(defaultTextElement);
                            const fonts = (SF.Util as any).Fonts.fromShape(defaultTextShape);

                            const captureTextPreview = (textShape: IEditorShape) => {
                                fabricCanvas.renderAll();
                                this.waitForCanvasToLoad(false).then(() => {
                                    try {
                                        if (defaultText.textEdits) {
                                            (SF.Util as any).Translator.getPreviewImageFromTextAsBase64(textShape).then(dataUrl => {
                                                defaultText.textEdits!.preview_image = dataUrl;
                                                textPromise();
                                            });
                                        } else {
                                            textReject('Text edits not found');
                                        }
                                    } catch (error) {
                                        textReject('Error creating text preview.');
                                    }
                                });
                            };
                            if (!fabricCanvas.fontService.areLoaded(fonts)) {
                                fabricCanvas.fontService.loadMultiple(fonts).then(() => {
                                    fabricCanvas.add(defaultTextShape);
                                    captureTextPreview(defaultTextShape);
                                }, err => {
                                    console.error('FontService.loadMultiple', err);
                                });
                            } else {
                                fabricCanvas.add(defaultTextShape);
                                captureTextPreview(defaultTextShape);
                            }
                        });

                    });
                    Promise.all(unEditedTextPromises).then(() => {
                        resolve();
                    }, err => {
                        reject(err);
                    });
                } else {
                    resolve();
                }
            });

        }

        public resizeCanvas(width: number, height: number, maxSize: number, maxWidth: number = 0, maxHeight: number = 0): void {
            if (this.shouldApplyMarginOnCanvas()) {
                let layerWidth = 0;
                let layerMargins: ILayerMargin = {
                    topMargin: 0,
                    bottomMargin: 0,
                    leftMargin: 0,
                    rightMargin: 0
                };

                const overlayLandscape: IMaskPreview | undefined = (this.maskPreviewData as IMaskPreviewData).mask.L;
                const overlayPortrait: IMaskPreview | undefined = (this.maskPreviewData as IMaskPreviewData).mask.P;
                if (overlayLandscape) {
                    layerWidth = (overlayLandscape as IEditorMaskPreview).width;
                    layerMargins = {
                        leftMargin: parseInt(overlayLandscape.margin_left as string || '0'),
                        rightMargin: parseInt(overlayLandscape.margin_right as string || '0'),
                        topMargin: parseInt(overlayLandscape.margin_top as string || '0'),
                        bottomMargin: parseInt(overlayLandscape.margin_bottom as string || '0')
                    };
                } else if (overlayPortrait) {
                    layerWidth = (overlayPortrait as IEditorMaskPreview).width;
                    layerMargins = {
                        leftMargin: parseInt(overlayPortrait.margin_left as string || '0'),
                        rightMargin: parseInt(overlayPortrait.margin_right as string || '0'),
                        topMargin: parseInt(overlayPortrait.margin_top as string || '0'),
                        bottomMargin: parseInt(overlayPortrait.margin_bottom as string || '0')
                    };
                }
                const spaceAroundEditableArea = this.calculateEditableAreaMarginsFromCanvasLayer(layerWidth, width, layerMargins);
                this.resizeAndClipCanvas(this.fabricCanvas as IEditorCanvas, this.options.maxWidth, this.options.maxHeight, width, height, 0, spaceAroundEditableArea);
            } else {
                this.size = EditorHelpers.getImageSizeFromMax(width, height, maxSize, maxWidth || this.options.maxWidth, maxHeight || this.options.maxHeight);
                // noinspection JSSuspiciousNameCombination
                (this.fabricCanvas as IEditorCanvas).setDimensions({
                    width: Math.floor(this.size.width),
                    height: Math.floor(this.size.height)
                });

                this.scale = EditorHelpers.zoomToFit(width, height, this.canvasContainer.offsetWidth, this.canvasContainer.offsetHeight);
                (this.fabricCanvas as IEditorCanvas).setZoom(this.scale);
            }
        }

        public setupMaskMergeOnCanvas(pageWidth: number, pageHeight: number, p: number): void {
            const maskPreview: MaskOverlayImage = {
                image: null,
                margin_bottom: 0,
                margin_top: 0,
                margin_left: 0,
                margin_right: 0
            };

            if (this.maskPreviewData && this.maskPreviewData.mask_merge) {
                const overlayImage: IWebsiteImage = this.buildOverlayMaskPreview(this.maskPreviewData.mask_merge[p]);
                const editorOverlayImage: IEditorImage = (SF.Util as any).Translator.toImageModel(overlayImage, '');
                this.setupPreviewImage(maskPreview, editorOverlayImage, this.maskPreviewData.mask_merge[p]);
                this.loadMaskImage(maskPreview, pageWidth, pageHeight).then(maskBW => {
                    if (this.fabricCanvas) {
                        if (!this.fabricCanvas.maskMerge) {
                            this.fabricCanvas.maskMerge = {};
                        }
                        this.fabricCanvas.maskMerge[p] = maskBW;
                    }
                });
            }
        }

        public setupMaskOnCanvas(pageWidth: number, pageHeight: number): void {
            const maskPreview: EditorMaskOverlay = {
                imageLandscape: {
                    image: null,
                    margin_bottom: 0,
                    margin_top: 0,
                    margin_left: 0,
                    margin_right: 0
                },
                imagePortrait: {
                    image: null,
                    margin_bottom: 0,
                    margin_top: 0,
                    margin_left: 0,
                    margin_right: 0
                }
            };

            const isPortrait = pageWidth <= pageHeight;
            if (this.maskPreviewData && this.maskPreviewData.mask_bw) {
                const maskLandscape: IMaskPreview | undefined = this.maskPreviewData.mask_bw.L;
                const maskPortrait: IMaskPreview | undefined = this.maskPreviewData.mask_bw.P;
                if (maskLandscape) {
                    const overlayImageLandscape: IWebsiteImage = this.buildOverlayMaskPreview(maskLandscape);
                    const iEditorOverlayImageLandscape: IEditorImage = (SF.Util as any).Translator.toImageModel(overlayImageLandscape, '');
                    this.setupPreview(maskPreview, iEditorOverlayImageLandscape, maskLandscape, 'imageLandscape');
                }
                if (maskPortrait) {
                    const overlayImagePortrait: IWebsiteImage = this.buildOverlayMaskPreview(maskPortrait);
                    const iEditorOverlayImagePortrait: IEditorImage = (SF.Util as any).Translator.toImageModel(overlayImagePortrait, '');
                    this.setupPreview(maskPreview, iEditorOverlayImagePortrait, maskPortrait, 'imagePortrait');
                }

                if (maskPreview.imageLandscape && maskPreview.imageLandscape.image && maskPreview.imagePortrait &&
                    !maskPreview.imagePortrait.image) {
                    this.setupPreview(maskPreview, maskPreview.imageLandscape.image, maskLandscape as IMaskPreview, 'imagePortrait');
                } else if (maskPreview.imagePortrait && maskPreview.imagePortrait.image && maskPreview.imageLandscape &&
                    !maskPreview.imageLandscape.image) {
                    this.setupPreview(maskPreview, maskPreview.imagePortrait.image, maskPortrait as IMaskPreview, 'imageLandscape');
                }
                this.loadMaskImage((isPortrait ? maskPreview.imagePortrait : maskPreview.imageLandscape) as MaskOverlayImage,
                    pageWidth, pageHeight).then(maskBwImage => {
                    if (this.fabricCanvas) {
                        if (isPortrait) {
                            this.fabricCanvas.maskBW.maskBWPortrait = maskBwImage;
                        } else {
                            this.fabricCanvas.maskBW.maskBWLandscape = maskBwImage;
                        }
                    }
                });
            }
        }

        public setupBackgroundOnCanvas(pageWidth: number, pageHeight: number): void {
            const backgroundPreview: EditorNonPrintableBackground = {
                imageLandscape: {
                    image: null,
                },
                imagePortrait: {
                    image: null,
                }
            };

            const isPortrait = pageWidth <= pageHeight;
            if (this.nonPrintableBackgroundPreviewData && this.nonPrintableBackgroundPreviewData.non_printable_background) {
                const nonPrintableBackgroundLandscape: INonPrintableBackgroundPreview | undefined = this.nonPrintableBackgroundPreviewData.non_printable_background.L;
                const nonPrintableBackgroundPortrait: INonPrintableBackgroundPreview | undefined = this.nonPrintableBackgroundPreviewData.non_printable_background.P;
                if (nonPrintableBackgroundLandscape) {
                    const backgroundImageLandscape: IWebsiteImage = this.buildNonPrintableBackgroundPreview(nonPrintableBackgroundLandscape);
                    const iEditorBackgroundLandscape: IEditorImage = (SF.Util as any).Translator.toImageModel(backgroundImageLandscape, '');
                    this.setupBackgroundPreview(backgroundPreview, iEditorBackgroundLandscape, 'imageLandscape');
                }
                if (nonPrintableBackgroundPortrait) {
                    const backgroundImagePortrait: IWebsiteImage = this.buildNonPrintableBackgroundPreview(nonPrintableBackgroundPortrait);
                    const iEditorBackgroundImagePortrait: IEditorImage = (SF.Util as any).Translator.toImageModel(backgroundImagePortrait, '');
                    this.setupBackgroundPreview(backgroundPreview, iEditorBackgroundImagePortrait, 'imagePortrait');
                }

                if (backgroundPreview.imageLandscape && backgroundPreview.imageLandscape.image && backgroundPreview.imagePortrait && !backgroundPreview.imagePortrait.image) {
                    this.setupBackgroundPreview(backgroundPreview, backgroundPreview.imageLandscape.image, 'imagePortrait');
                } else if (backgroundPreview.imagePortrait && backgroundPreview.imagePortrait.image && backgroundPreview.imageLandscape && !backgroundPreview.imageLandscape.image) {
                    this.setupBackgroundPreview(backgroundPreview, backgroundPreview.imagePortrait.image, 'imageLandscape');
                }
                this.loadBackgroundImage(backgroundPreview, isPortrait, pageWidth, pageHeight);
            }
        }

        private buildNonPrintablePreview(previewThumbs: IPreviewThumb[]): IWebsiteImage {
            const width = previewThumbs[0].width;
            const height = previewThumbs[0].height;
            const thumbList: IWebsiteThumb[] = [];
            const fullUrl = previewThumbs[previewThumbs.length - 1].url;
            for (let i = 0; i < previewThumbs.length; i++) {
                const thumb: IWebsiteThumb = {
                    url: previewThumbs[i].url,
                    width: previewThumbs[i].width,
                    height: previewThumbs[i].height,
                    thumb_type: previewThumbs[i].thumb_type,
                    file_path: previewThumbs[i].file_path,
                };
                thumbList.push(thumb);

            }

            return {
                id: null,
                full_url: fullUrl,
                image_data: {},
                edits: {},
                width: width,
                height: height,
                thumb_list: thumbList,
                placeHolder: false,
            } as any;
        }

        private buildNonPrintableBackgroundPreview(nonPrintableBackgroundPreview: INonPrintableBackgroundPreview): IWebsiteImage {
            return this.buildNonPrintablePreview(nonPrintableBackgroundPreview.thumbs);
        }

        private buildOverlayMaskPreview(maskPreview: IMaskPreview): IWebsiteImage {
            return this.buildNonPrintablePreview(maskPreview.thumbs);
        }

        private setupPreview(overlayPreview: EditorMaskOverlay, image: IEditorImage, overlay: IMaskPreview, imageType: EditorMaskOverlayTypes): void {
            const overlayPreviewElement = overlayPreview[imageType];
            if (overlayPreviewElement) {
                this.setupPreviewImage(overlayPreviewElement, image, overlay);
            } else {
                console.error('Unable to find overlay preview element ' + imageType);
            }
        }

        private setupPreviewImage(overlayPreviewElement: MaskOverlayImage, image: IEditorImage, overlay: IMaskPreview): void {
            overlayPreviewElement.image = image;
            overlayPreviewElement.margin_right = parseInt(overlay.margin_right as string || '0');
            overlayPreviewElement.margin_left = parseInt(overlay.margin_left as string || '0');
            overlayPreviewElement.margin_top = parseInt(overlay.margin_top as string || '0');
            overlayPreviewElement.margin_bottom = parseInt(overlay.margin_bottom as string || '0');
        }

        private setupBackgroundPreview(backgroundPreview: EditorNonPrintableBackground, image: IEditorImage, imageType: EditorMaskOverlayTypes): void {
            if (backgroundPreview[imageType]) {
                ((backgroundPreview[imageType] as NonPrintableBackgroundImage).image as IEditorImage) = image;
            } else {
                console.error('Unable to find background preview element ' + imageType);
            }
        }

        // noinspection TypeScriptValidateTypes
        private loadMaskImage(maskEditorImage: MaskOverlayImage, pageWidth: number, pageHeight: number): Promise<CanvasMaskBWImage> {
            return new Promise<CanvasMaskBWImage>(resolve => {

                const canvasMaskBW: CanvasMaskBWImage = {
                    // @ts-ignore
                    image: null,
                    margin_left: 0,
                    margin_right: 0,
                    margin_top: 0,
                    margin_bottom: 0
                };

                const image = new SF.Editor.Image(maskEditorImage.image as any, {});

                // @ts-ignore
                image.canvas = this.fabricCanvas;
                image.image.width = pageWidth;
                image.image.height = pageHeight;
                (this.fabricCanvas as IEditorCanvas).maskBW = {};

                ((image as any)._loadImage)(image.image, true, true, (maskImage: any) => {
                    maskImage.width = maskImage._element.width;
                    maskImage.height = maskImage._element.height;
                    maskImage.set({
                        height: pageHeight,
                        width: pageWidth,
                    });
                    //
                    if (this.fabricCanvas) {
                        canvasMaskBW.image = maskImage._element;
                        canvasMaskBW.margin_bottom = maskEditorImage.margin_bottom;
                        canvasMaskBW.margin_top = maskEditorImage.margin_top;
                        canvasMaskBW.margin_left = maskEditorImage.margin_left;
                        canvasMaskBW.margin_right = maskEditorImage.margin_right;
                        resolve(canvasMaskBW);
                    }
                    this.drawMaskOnCanvas(pageWidth, pageHeight);
                });
            });
        }

        // noinspection TypeScriptValidateTypes
        private loadBackgroundImage(backgroundPreview: EditorNonPrintableBackground, isPortrait: boolean, pageWidth: number, pageHeight: number): void {

            const canvasNonPrintableBackground: CanvasNonPrintableBackgroundImage = {
                // @ts-ignore
                image: null,
            };

            let backgroundImage;
            if (isPortrait) {
                if (!backgroundPreview.imagePortrait) {
                    console.error('Unable to find background preview portrait');
                    return;
                }
                backgroundImage = backgroundPreview.imagePortrait.image;
            } else {
                if (!backgroundPreview.imageLandscape) {
                    console.error('Unable to find background preview landscape');
                    return;
                }
                backgroundImage = backgroundPreview.imageLandscape.image;
            }
            const image = new SF.Editor.Image(backgroundImage as any, {});

            //@ts-ignore
            image.canvas = this.fabricCanvas;
            image.image.width = pageWidth;
            image.image.height = pageHeight;
            (this.fabricCanvas as IEditorCanvas).nonPrintableBackground = {};

            ((image as any)._loadImage)(image.image, true, true, (fabricBackgroundImage: any) => {
                fabricBackgroundImage.width = fabricBackgroundImage._element.width;
                fabricBackgroundImage.height = fabricBackgroundImage._element.height;
                fabricBackgroundImage.set({
                    height: pageHeight,
                    width: pageWidth,
                });
                if (this.fabricCanvas) {
                    if (isPortrait) {
                        canvasNonPrintableBackground.image = fabricBackgroundImage._element;
                        this.fabricCanvas.nonPrintableBackground.nonPrintableBackgroundPortrait = canvasNonPrintableBackground;
                    } else {
                        canvasNonPrintableBackground.image = fabricBackgroundImage._element;
                        this.fabricCanvas.nonPrintableBackground.nonPrintableBackgroundLandscape = canvasNonPrintableBackground;
                    }
                }
                this.drawBackgroundOnCanvas(pageWidth, pageHeight);
            });
        }

        private drawRectOnCanvas(pageWidth: number, pageHeight: number): void {
            if (this.fabricCanvas) {
                const ctx: CanvasRenderingContext2D = this.fabricCanvas.getContext();
                const scale = this.fabricCanvas.getZoom();
                const width = Math.round(pageWidth * scale);
                const height = Math.round(pageHeight * scale);

                ctx.save();
                const defaultCompositeOperation = ctx.globalCompositeOperation;
                ctx.globalCompositeOperation = 'destination-over';
                ctx.fillStyle = CANVAS_BACKGROUND_COLOR_WITH_MARGIN;
                const margin = this.getMaskMargin(scale, width, height);
                ctx.fillRect(margin.left, margin.top, width, height);
                ctx.globalCompositeOperation = defaultCompositeOperation;
                ctx.restore();

            }
        }

        private drawBackgroundOnCanvas(pageWidth: number, pageHeight: number): void {
            if (this.fabricCanvas && this.fabricCanvas.nonPrintableBackground && (this.fabricCanvas.nonPrintableBackground.nonPrintableBackgroundPortrait || this.fabricCanvas.nonPrintableBackground.nonPrintableBackgroundLandscape)) {
                const ctx: CanvasRenderingContext2D = this.fabricCanvas.getContext();
                const scale = this.fabricCanvas.getZoom();
                const width = Math.round(pageWidth * scale);
                const height = Math.round(pageHeight * scale);
                const isPortrait: boolean = pageWidth <= pageHeight;

                if (this.fabricCanvas.nonPrintableBackground) {
                    ctx.save();
                    const defaultCompositeOperation = ctx.globalCompositeOperation;
                    ctx.globalCompositeOperation = 'destination-over';
                    let background: CanvasNonPrintableBackgroundImage | undefined;
                    if (isPortrait) {
                        background = this.fabricCanvas.nonPrintableBackground.nonPrintableBackgroundPortrait;
                    } else {
                        background = this.fabricCanvas.nonPrintableBackground.nonPrintableBackgroundLandscape;
                    }
                    if (background && background.image) {
                        if (this.overlayPosition || !this.maskPreviewData?.mask) {
                            const margin = this.getMaskMargin(scale, width, height);
                            ctx.drawImage(background.image, margin.left, margin.top, width, height);
                        }
                    }

                    ctx.globalCompositeOperation = defaultCompositeOperation;
                    ctx.restore();
                }
            }

        }

        private getMaskMargin(scale: number, width: number, height: number): { left: number, top: number } {
            let mask: null | { left: number, top: number } = null;

            if (width > height && this.maskPreviewData?.mask?.L) {
                mask = {
                    left: parseInt(this.maskPreviewData.mask.L.margin_left as string || '0'),
                    top: parseInt(this.maskPreviewData.mask.L.margin_top as string || '0')
                };
            } else if (height > width && this.maskPreviewData?.mask?.P) {
                mask = {
                    left: parseInt(this.maskPreviewData.mask.P.margin_left as string || '0'),
                    top: parseInt(this.maskPreviewData.mask.P.margin_top as string || '0')
                };
            }
            if (this.overlayPosition && mask) {
                return {
                    left: this.overlayPosition.left + (scale * mask.left),
                    top: this.overlayPosition.top + (scale * mask.top)
                };
            } else {
                return {
                    left: 0,
                    top: 0
                };
            }

        }

        private drawMaskOnCanvas(pageWidth: number, pageHeight: number): void {
            if (this.fabricCanvas && (this.fabricCanvas.maskBW || this.fabricCanvas.maskMerge)) {
                const ctx: CanvasRenderingContext2D = this.fabricCanvas.getContext();
                const width = Math.round(pageWidth * this.fabricCanvas.getZoom());
                const height = Math.round(pageHeight * this.fabricCanvas.getZoom());

                let mask: CanvasMaskBWImage | undefined;
                if (this.fabricCanvas.maskMerge) {
                    mask = this.fabricCanvas.maskMerge[this.fabricCanvas.firstPage?.id || 0];
                } else if (this.fabricCanvas.maskBW) {
                    const isPortrait: boolean = pageWidth <= pageHeight;
                    if (isPortrait) {
                        mask = this.fabricCanvas.maskBW.maskBWPortrait;
                    } else {
                        mask = this.fabricCanvas.maskBW.maskBWLandscape;
                    }
                }

                if (mask && mask.image) {
                    ctx.save();
                    const defaultCompositeOperation = ctx.globalCompositeOperation;
                    ctx.globalCompositeOperation = 'destination-out';
                    ctx.drawImage(
                        mask.image
                        , -(mask.margin_left as number) * this.fabricCanvas.getZoom() + this.fabricCanvas.marginLeft - 1
                        , -(mask.margin_top as number) * this.fabricCanvas.getZoom() + this.fabricCanvas.marginTop - 1
                        , width + ((mask.margin_left as number) + (mask.margin_right as number)) * this.fabricCanvas.getZoom() + 2
                        , height + ((mask.margin_top as number) + (mask.margin_bottom as number)) * this.fabricCanvas.getZoom() + 2);

                    ctx.globalCompositeOperation = defaultCompositeOperation;
                    ctx.restore();
                }
            }

        }

        private calculateEditableAreaMarginsFromCanvasLayer(layerWidth: number, pageWidth: number,
                                                            layerMargins: ILayerMargin):
            { extraSpaceTop: number, extraSpaceBottom: number, extraSpaceLeft: number, extraSpaceRight: number } {
            const widthOfEditableAreaOnLayerImage = layerWidth - layerMargins.leftMargin - layerMargins.rightMargin;
            const scale = widthOfEditableAreaOnLayerImage ? pageWidth / widthOfEditableAreaOnLayerImage : 0;
            return {
                extraSpaceBottom: layerMargins.bottomMargin * scale,
                extraSpaceTop: layerMargins.topMargin * scale,
                extraSpaceLeft: layerMargins.leftMargin * scale,
                extraSpaceRight: layerMargins.rightMargin * scale
            };
        }

        private resizeAndClipCanvas(
            editorCanvas: IEditorCanvas,
            canvasContainerWidth: number,
            canvasContainerHeight: number,
            width: number,
            height: number,
            padding: number,
            extraSpace: {
                extraSpaceTop: number,
                extraSpaceBottom: number,
                extraSpaceLeft: number,
                extraSpaceRight: number
            },
        ): void {
            padding *= 2;

            editorCanvas.setDimensions({
                width: canvasContainerWidth,
                height: canvasContainerHeight,
            });
            const innerWidth = width + extraSpace.extraSpaceLeft + extraSpace.extraSpaceRight;
            const innerHeight = height + extraSpace.extraSpaceTop + extraSpace.extraSpaceBottom;
            const outerWidth = canvasContainerWidth - padding;
            const outerHeight = canvasContainerHeight - padding;
            this.scale = innerWidth / innerHeight > outerWidth / outerHeight ? outerWidth / innerWidth : outerHeight / innerHeight;
            editorCanvas.setZoom(this.scale);
            const marginTop = Math.round((canvasContainerHeight - this.scale * (extraSpace.extraSpaceTop + extraSpace.extraSpaceBottom + height)) / 2 + this.scale * extraSpace.extraSpaceTop);
            const marginLeft = Math.round((canvasContainerWidth - this.scale * (extraSpace.extraSpaceLeft + extraSpace.extraSpaceRight + width)) / 2 + this.scale * extraSpace.extraSpaceLeft);

            if (editorCanvas.viewportTransform) {
                editorCanvas.viewportTransform[4] = marginLeft;
                editorCanvas.viewportTransform[5] = marginTop;
            }
            editorCanvas.marginLeft = marginLeft;
            editorCanvas.marginTop = marginTop;
            editorCanvas.marginRight = canvasContainerWidth - marginLeft - this.scale * width;
            editorCanvas.marginBottom = canvasContainerHeight - marginTop - this.scale * height;
            editorCanvas.calcViewportBoundaries();

            // couldn't get the new clipPath in fabricjs to work so using this instead. They also mentioned that clipPath is not efficient compared to their old clipTo
            editorCanvas.clipTo = (ctx: CanvasRenderingContext2D): void => {
                ctx.rect(marginLeft, marginTop, this.scale * width, this.scale * height);
            };
        }

        public renderOverlay(pageWidth: number, pageHeight: number, pageIndex?: number): void {
            let marginLeft = 0;
            let marginRight = 0;
            let marginTop = 0;
            let marginBottom = 0;
            const overlayPreview: EditorMaskOverlay = {
                imageLandscape: {
                    image: null,
                    margin_bottom: 0,
                    margin_top: 0,
                    margin_left: 0,
                    margin_right: 0
                },
                imagePortrait: {
                    image: null,
                    margin_bottom: 0,
                    margin_top: 0,
                    margin_left: 0,
                    margin_right: 0
                }
            };

            const isPortrait = pageWidth <= pageHeight;
            if (this.maskPreviewData && (this.maskPreviewData.mask || (this.maskPreviewData.overlay_merge && pageIndex !== undefined && this.maskPreviewData.overlay_merge[pageIndex]))) {
                const overlayLandscape: IMaskPreview | undefined = this.maskPreviewData.mask ? this.maskPreviewData.mask.L : this.maskPreviewData.overlay_merge[pageIndex as number];
                let overlayPortrait: IMaskPreview | undefined = this.maskPreviewData.mask ? this.maskPreviewData.mask.P : this.maskPreviewData.overlay_merge[pageIndex as number];
                if (overlayLandscape) {
                    const overlayImageLandscape: IWebsiteImage = this.buildOverlayMaskPreview(overlayLandscape);
                    this.setupPreview(overlayPreview, (SF.Util as any).Translator.toImageModel(overlayImageLandscape, ''), overlayLandscape, 'imageLandscape');
                }
                if (overlayPortrait) {
                    const overlayImagePortrait: IWebsiteImage = this.buildOverlayMaskPreview(overlayPortrait);
                    this.setupPreview(overlayPreview, (SF.Util as any).Translator.toImageModel(overlayImagePortrait, ''), overlayPortrait, 'imagePortrait');
                }


                if (overlayPreview.imageLandscape && overlayPreview.imageLandscape.image && overlayPreview.imagePortrait && !overlayPreview.imagePortrait.image) {
                    this.setupPreview(overlayPreview, overlayPreview.imageLandscape.image, overlayLandscape as IMaskPreview, 'imagePortrait');
                } else if (overlayPreview.imagePortrait && overlayPreview.imagePortrait.image && overlayPreview.imageLandscape && !overlayPreview.imageLandscape.image) {
                    this.setupPreview(overlayPreview, overlayPreview.imagePortrait.image, overlayPortrait as IMaskPreview, 'imageLandscape');
                }
                let overlayImage;

                if (isPortrait) {
                    if (!overlayPreview.imagePortrait || !overlayPreview.imagePortrait.image) {
                        console.error('No overlay image for portrait');
                        return;
                    }
                    overlayImage = overlayPreview.imagePortrait.image;
                    const imagePortrait = overlayPreview.imagePortrait;
                    marginBottom = imagePortrait.margin_bottom || 0;
                    marginTop = imagePortrait.margin_top || 0;
                    marginLeft = imagePortrait.margin_left || 0;
                    marginRight = imagePortrait.margin_right || 0;
                } else {
                    if (!overlayPreview.imageLandscape || !overlayPreview.imageLandscape.image) {
                        console.error('No overlay image for landscape');
                        return;
                    }
                    overlayImage = overlayPreview.imageLandscape.image;
                    const imageLandscape = overlayPreview.imageLandscape;
                    marginBottom = imageLandscape.margin_bottom || 0;
                    marginTop = imageLandscape.margin_top || 0;
                    marginLeft = imageLandscape.margin_left || 0;
                    marginRight = imageLandscape.margin_right || 0;
                }

                const image = new SF.Editor.Image(overlayImage as any, {});
                // @ts-ignore
                image.canvas = this.fabricCanvas;
                image.image.width = pageWidth + marginLeft + marginRight;
                image.image.height = pageHeight + marginTop + marginBottom;

                const margin = {
                    left: marginLeft,
                    top: marginTop,
                    right: marginRight,
                    bottom: marginBottom
                };

                ((image as any)._loadImage)(image.image, true, true, (fabricOverlayImage: any) => {
                    this.setOverlayImageOnCanvas(fabricOverlayImage, pageHeight, pageWidth, margin);
                    if (this.fabricCanvas) {
                        this.fabricCanvas.requestRenderAll();
                        this.waitForCanvasToLoad(true).then(async () => {
                            this.overlayPosition = fabricOverlayImage.getBoundingRect(false, true);
                            let count = 0;
                            while ((this.overlayPosition?.top || -5) < -1 && count++ < 100) {
                                await new Promise(resolve => setTimeout(resolve, 100));
                                console.log('Retrying...');
                                this.overlayPosition = fabricOverlayImage.getBoundingRect(false, true);
                            }
                            console.log('Overlay position.top ', this.overlayPosition, count);
                            this.fabricCanvas!.requestRenderAll();
                            if (count > 0 && (this.overlayPosition?.top || -5) >= -1) {
                                console.log('Forced re-render');
                            }
                        });
                    }
                });

            }

        }

        private setOverlayImageOnCanvas(overlayImage: any, height: number, width: number, margin: {
            left: number,
            top: number,
            right: number,
            bottom: number
        }): void {
            overlayImage.set({
                height: height + margin.top + margin.bottom,
                width: width + margin.left + margin.right,
                originX: 'center',
                originY: 'center',
            });
            overlayImage.top = (height + margin.top + margin.bottom) / 2 - margin.top;
            overlayImage.left = (width + margin.left + margin.right) / 2 - margin.left;
            if (this.fabricCanvas) {
                overlayImage.setCoords();
                this.fabricCanvas.setOverlayImage(overlayImage, this.fabricCanvas.requestRenderAll.bind(this.fabricCanvas), {} as any);   // Too much pain is involved by making all the IObjectOptions optional
            }
        }

        private loadFromPage(reset: boolean = false): void {
            const maxSize = this.getVisiblePagesWidth() > this.getVisiblePagesHeight() ? this.options.maxWidth : this.options.maxHeight;
            this.resizeCanvas(this.getVisiblePagesWidth(), this.getVisiblePagesHeight(), maxSize);
            this.loadEdits(this.visiblePages, reset);
        }

        private hasSpine(): boolean {
            const found = this.visiblePages.find((page: IEditorPage) => {
                return page.type === PageTypes.SPINE;
            });

            return found !== undefined;
        }

        private hasFakeSpine(): boolean {
            const found = this.visiblePages.find((page: IEditorPage) => {
                return page.type === PageTypes.SPINE && page.width === 0;
            });

            return found !== undefined;
        }

        private getElementPage(element: IEditorElement): IEditorPage | undefined {
            if (this.hasSpine()) {
                if (this.getFrontCoverElements().find((coverElement: IEditorElement) => {
                    return element.uid === coverElement.uid;
                })) {
                    return this.getFrontCoverPage();
                } else if (this.getBackCoverElements().find((coverElement: IEditorElement) => {
                    return element.uid === coverElement.uid;
                })) {
                    return this.getBackCoverPage();
                } else if (this.getSpineElements().find((coverElement: IEditorElement) => {
                    return element.uid === coverElement.uid;
                })) {
                    return this.getSpine();
                }
            }

            return this.visiblePages[0];

        }

        private getFrontCoverElements(): IEditorElement[] {
            const backCoverPage = this.getBackCoverPage();
            const spine = this.getSpine();
            const offset = (backCoverPage ? backCoverPage.width : 0) + (spine ? spine.width : 0);
            const frontCoverObjects = (this.fabricCanvas as IEditorCanvas).getObjects().filter((object: fabric.Object) => {
                return (object.left || 0) > offset;
            });

            return frontCoverObjects.map((shape: fabric.Object) => {
                const element = (SF.Util as any).Translator.toElement(shape as IEditorShape);
                if (element) {
                    element.x -= offset;
                }
                return element;
            }).filter(element => !!element) as IEditorElement[];

        }

        private getBackCoverElements(): IEditorElement[] {
            const backCoverPage = this.getBackCoverPage();
            const width = backCoverPage ? backCoverPage.width : 0;
            const backCoverObjects = (this.fabricCanvas as IEditorCanvas).getObjects().filter((object: fabric.Object) => {
                return (object.left || 0) < width;
            });

            return backCoverObjects
                .map(shape => (SF.Util as any).Translator.toElement(shape as IEditorShape))
                .filter(element => !!element) as IEditorElement[];

        }

        private getSpineElements(): IEditorElement[] {
            const backCoverPage = this.getBackCoverPage();
            const backgroundCoverWith = backCoverPage ? backCoverPage.width : 0;
            const spine = this.getSpine();
            const spineWidth = spine ? spine.width : 0;
            const spineObjects = (this.fabricCanvas as IEditorCanvas).getObjects().filter((object: fabric.Object) => {
                return (object.left || 0) >= backgroundCoverWith && (object.left || 0) <= backgroundCoverWith + spineWidth;
            });

            const offset = backgroundCoverWith;
            return spineObjects.map((shape: fabric.Object) => {
                const element = (SF.Util as any).Translator.toElement(shape as IEditorShape);
                if (element) {
                    element.x -= offset;
                }
                return element;
            }).filter(element => !!element) as IEditorElement[];
        }

        private getFrontCoverPage(): IEditorPage | undefined {
            return this.visiblePages.find((page: IEditorPage) => {
                return page.type == PageTypes.FRONT_COVER;
            });
        }

        private getBackCoverPage(): IEditorPage | undefined {
            return this.visiblePages.find((page: IEditorPage) => {
                return page.type == PageTypes.BACK_COVER;
            });
        }

        private getSpine(): IEditorPage | undefined {
            return this.visiblePages.find((page: IEditorPage) => {
                return page.type == PageTypes.SPINE;
            });
        }

        private getPageXOffset(page: IEditorPage): number {
            const backCoverPage = this.getBackCoverPage();
            if (page.type === PageTypes.FRONT_COVER && backCoverPage) {
                const spine = this.getSpine();
                return backCoverPage.width + (spine ? spine.width : 0);
            } else if (page.type === PageTypes.SPINE && backCoverPage) {
                return backCoverPage.width;
            }
            return 0;
        }

        private canEditVisiblePages(): boolean {
            return this.visiblePages.some((page: IEditorPage) => {
                return page.canEdit;
            });
        }

        private getCoverPagesFreeObjects(pageType: string): IFreeObject[] {
            if (pageType == PageTypes.FRONT_COVER) {
                return this.getFrontCoverElements().map((element: IEditorElement) => {
                    return (SF.Util as any).Edits.fromElement(element);
                });
            } else if (pageType == PageTypes.BACK_COVER) {
                return this.getBackCoverElements().map((element: IEditorElement) => {
                    return (SF.Util as any).Edits.fromElement(element);
                });
            } else if (pageType == PageTypes.SPINE) {
                return this.getSpineElements().map((element: IEditorElement) => {
                    return (SF.Util as any).Edits.fromElement(element);
                });
            }
            return [];
        }

        // Private elements
        private setupFabricEvents(): void {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas not created');
                return;
            }
            this.fabricCanvas.on('selection:created', (e: fabric.IEvent) => {
                const target = this.getTarget<IEditorShape>(e);
                if (target) {
                    this.objectSelected(target, true);
                } else {
                    console.warn('Target is not set');
                }
            });
            this.fabricCanvas.on('selection:updated', (e: fabric.IEvent) => {
                const target = this.getTarget<IEditorShape>(e);
                if (target) {
                    this.objectSelected(target, true);
                } else {
                    console.warn('Target is not set');
                }
            });
            this.fabricCanvas.on('selection:cleared', () => {
                this.objectUnselected();
            });
            this.fabricCanvas.on('mouse:down', (e: fabric.IEvent) => {
                if (this.options.ghostingEnabled) {
                    this.enableGhosting(e);
                }
            });
            this.fabricCanvas.on('object:moving', (e: fabric.IEvent) => {
                this.checkObjectPosition(e);
            });

            this.fabricCanvas.on('object:modified', (e: fabric.IEvent) => {
                const target = this.getTarget<IEditorShape>(e);
                if (target) {

                    // Currently only interested in text changes...
                    if (this._onZoneModified && target.uid && (e as any).disableUndoManager === undefined) {
                        const selectElement = this.getElementFromZone(target);
                        if (target.type === 'sf-text' && selectElement && this.isValidFreeObjectId(selectElement.freeObjectId) && this.currentZone) {
                            this._onZoneModified(selectElement.freeObjectId || 0, {
                                type: 'text',
                                text: (target as unknown as IEditorText).text,
                                zone: this.currentZone
                            });
                        } else if ((target.type === 'photo-group' || target.type === 'background') && selectElement &&
                            this.isValidFreeObjectId(selectElement.freeObjectId) && this.currentZone) {
                            this._onZoneModified(selectElement.freeObjectId || 0, {
                                type: 'image',
                                zoom: target.getZoom(),
                                zone: this.currentZone
                            });
                        }
                    }
                } else {
                    console.warn('target is not set');
                }
            });

            this.fabricCanvas.on('text:editing:exited', (e: fabric.IEvent) => {
                const target = this.getTarget<IEditorText>(e);
                if (target && target.linkedZoneId) {
                    this.updateLinkedZone(e.target);
                }
            });

            this.fabricCanvas.on('text:changed', (e: fabric.IEvent) => {
                const target = this.getTarget<IEditorText>(e);
                if (target) {
                    if (this._onTextChanged) {
                        this._onTextChanged(this.currentFreeObjectId, target);
                    }
                    if (target.linkedZoneId) {
                        this.updateLinkedZone(e.target);
                    }
                } else {
                    console.warn('target is not set');
                }
            });
        }

        private isValidFreeObjectId(freeObjectId: string | number | undefined | null): boolean {
            return !!freeObjectId || freeObjectId === '0' || freeObjectId === 0;
        }

        private updateLinkedZone(target: any): void {
            const linkedZone = (this.fabricCanvas as IEditorCanvas).getObjects().find(o => (o as IEditorText).textZoneId === target.linkedZoneId) as IEditorText;
            if (linkedZone && !linkedZone.edited) {
                linkedZone.linkEdited = true;
                linkedZone.text = linkedZone.fixTextFromOptions(target.text);
                if (this._onTextChanged) {
                    this._onTextChanged(linkedZone.freeObjectId as number, linkedZone);
                }
            }
        }

        private activateDoubleClickOnText(text: IEditorText): void {
            text.on('dblclick', () => {
                if (this._onTextDoubleClick) {
                    this._onTextDoubleClick();
                }
            });
        }

        private setupFileDragDrop(): void {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return;
            }

            document.body.addEventListener('dragover', e => {
                if ((e.target as HTMLElement).nodeName.toLowerCase() !== 'canvas') {
                    e.preventDefault();
                    if (e.dataTransfer) {
                        e.dataTransfer.dropEffect = 'none';
                    }
                }
            });
            this.fabricCanvas.wrapperEl.addEventListener('dragover', e => {
                this.handleFileDragOver(e);
            }, false);
            this.fabricCanvas.wrapperEl.addEventListener('drop', e => {
                this.handleFileDrop(e);
            }, false);

        }

        private drawMaskOnCurrentPageSizeCanvas(): void {
            this.drawMaskOnCanvas(this.getVisiblePagesWidth(), this.getVisiblePagesHeight());
        }

        private drawNonPrintableBackgroundOnCurrentPageSizeCanvas(): void {
            if (this.nonPrintableBackgroundPreviewData) {
                this.drawBackgroundOnCanvas(this.getVisiblePagesWidth(), this.getVisiblePagesHeight());
            } else {
                this.drawRectOnCanvas(this.getVisiblePagesWidth(), this.getVisiblePagesHeight());
            }
        }

        private getElementFromZone(zone: IEditorShape): IEditorElement | null {

            const element = this.getPageElements().find(searchElement => searchElement.uid === zone.uid) || null;
            if (element && !this.isValidFreeObjectId(element.freeObjectId)) {
                Object.keys(this.freeObjectIdToUid).some(freeObjectId => {
                    if (this.freeObjectIdToUid[freeObjectId] === element.uid) {
                        element.freeObjectId = parseInt(freeObjectId, 10);
                        return true;
                    }
                });
            }
            return element;
        }

        private loadEdits(pages: IEditorPage[], reset: boolean = false): void {

            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return;
            }

            this.freeObjectIdToUid = {};

            const pageElementsCollection = RendererImage.toCollection(pages);
            // updateDefaultText();
            pageElementsCollection.elements.forEach((element: IEditorElement) => {
                this.freeObjectIdToUid[element.freeObjectId as number] = element.uid;
                if (element instanceof SF.Model.TextElement) {
                    const textElement = element as unknown as IEditorTextElement;
                    // Do not replace text with placeholder/default if it's a printsuite text element
                    // printsuite elements have customerEditable field
                    if (textElement.text && !textElement.edited && textElement.editable && textElement.customerEditable === undefined) {
                        textElement.text = textElement.placeHolderTranslations ? textElement.placeHolderTranslations[SF.currentLanguage] ||
                            DefaultText['large'] : DefaultText['large'];
                        if (pageElementsCollection
                            && pageElementsCollection.pages) {
                            pageElementsCollection.pages.forEach((page: IEditorPage) => {
                                if (page.elements && page.elements.indexOf(element) >= 0) {
                                    (page.elements[page.elements.indexOf(element)] as any).text =
                                        textElement.placeHolderTranslations ? textElement.placeHolderTranslations[SF.currentLanguage] ||
                                            DefaultText['large'] : DefaultText['large'];
                                }
                            });
                        }

                    } else if (reset) {
                        textElement.styles = {};
                    }
                }
            });

            (SF.Util as any).Translator.boardSetter(this.fabricCanvas, pageElementsCollection, false);
            this.modifyEventsAndControlsOnCanvasObjects(true);
            this.fabricCanvas.renderAll();

        }

        private searchPossibleTargets(e: any, activeObject: any): fabric.Object | null {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return null;
            }
            const pointer = this.fabricCanvas.getPointer(e, true);
            const normalizedPointer = this.fabricCanvas._normalizePointer(activeObject, pointer);
            let i = activeObject._objects.length;

            while (i--) {
                if (this.fabricCanvas._checkTarget(normalizedPointer, activeObject._objects[i], true)) {
                    return activeObject._objects[i];
                }
            }
            return null;
        }

        private handleFileDragOver(e: DragEvent): void {

            e.preventDefault();
            let dropEffect: 'copy' | 'none' = 'none';
            const target = this.getTargetFromDrag(e);
            if (target) {
                dropEffect = 'copy';
            }
            if (e.dataTransfer) {
                e.dataTransfer.dropEffect = dropEffect;
            }
        }

        private getTargetFromDrag(e: DragEvent): fabric.Object | null {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return null;
            }
            let target: fabric.Object | null = this.fabricCanvas.findTarget(e);
            if (target) {
                if (target.type && target.type == 'group') {
                    target = this.searchPossibleTargets(e, target);
                }
            }

            // this is brought over from flash, and currently mainly for calendars
            // if we have any uneditable / unselectable zones, we should not be able to place anything over it
            if (target && !target.selectable && target.type != 'clipart') {
                return null;
            }
            return target;
        }

        private handleFileDrop(e: DragEvent): boolean | undefined {
            e.preventDefault(); //I've altered this line for FireFox
            if (e.dataTransfer && e.dataTransfer.files.length > 0) {
                const imageFile = e.dataTransfer.files[0];
                const target = this.getTargetFromDrag(e) as IEditorShape;
                const selectElement = this.getElementFromZone(target);
                if (selectElement && imageFile.name.match(/\.(jpg|jpeg|png)/)) {
                    const freeObjectId = selectElement.freeObjectId || 0;
                    this.setCurrentZoneByFreeObjectId(freeObjectId);

                    const reader = new FileReader();
                    reader.addEventListener('load', event => {
                        const img = new Image();
                        img.addEventListener('load', () => {
                            const freeObject = (SF.Util as any).Edits.fromElement(selectElement);
                            if (freeObject.uniquePhotoId) {
                                RendererImage.removeEditorImage(freeObject.uniquePhotoId);
                            }
                            this.setCurrentZoneImage(RendererImage.getEditorImageFromImage(img, imageFile.name));
                            if (this._onImageDropped) {
                                this._onImageDropped(freeObjectId, imageFile.name);
                            }
                        });
                        img.addEventListener('error', () => {
                            // TODO: Show user error
                            console.error('Error, invalid image');
                            if (this._onImageDropped) {
                                this._onImageDropped(freeObjectId, '');
                            }
                        });
                        img.src = (event.target as any).result as string;
                    });
                    reader.readAsDataURL(imageFile);
                } else {
                    if (selectElement && this._onImageDropped) {
                        this._onImageDropped(selectElement.freeObjectId as number, '');
                    }
                }

            }

            return false;
        }

        private objectSelected(target: IEditorShape | null, notify: boolean): void {

            if ((target as any) instanceof SF.Editor.PhotoGroup && (!(target as any).photoObject.image ||
                (target as any).photoObject.image.type !== BACKGROUND_TYPE)) {  // Don't select backgrounds, for now...
                const targetAsIEditorPhotoGroup = target as unknown as IEditorPhotoGroup;
                if (this.currentSelectionType !== SelectionType.Photo) {
                    this.currentSelectionType = SelectionType.Photo;
                }
                this.savePreviousZoom(targetAsIEditorPhotoGroup as any);
            }

            this.currentZone = target;
            if (target) {
                const selectElement = this.getElementFromZone(target);
                if (selectElement) {
                    this.currentFreeObjectId = selectElement.freeObjectId as number;
                    if (notify && this._onZoneChange) {
                        this._onZoneChange(selectElement.freeObjectId as number, selectElement, target);
                        this.modifyEventsAndControlsOnCanvasObjects(true, [target]);
                    }
                }
            }
        }

        private objectUnselected(): void {
            this.currentZone = null;
            this.currentSelectionType = SelectionType.None;
            if (this._onZoneChange) {
                this._onZoneChange(null, null, null);
            }
        }

        private savePreviousZoom(target: IEditorElement): void {
            // noinspection SuspiciousTypeOfGuard
            if (target instanceof SF.Editor.PhotoGroup) {
                this.getPageElements().forEach((element: IEditorElement) => {
                    if (element.uid === (target as unknown as IEditorPhotoGroup).uid) {
                        (this.fabricCanvas as any)._currentZoom = element.imagePosition.zoom;
                        return;
                    }
                });
            }
        }

        private render(): void {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return;
            }
            this.fabricCanvas.renderAll();
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public photoZoneHasWarning(zone: IEditorPhotoGroup|IEditorShape): boolean {
            return zone.getZoom() > ((zone.canvas as unknown as ICustomizerEditorCanvas).errorConfiguration ? (zone.canvas as unknown as ICustomizerEditorCanvas).errorConfiguration.zoomWarning : 100);
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public photoZoneHasError(zone: IEditorPhotoGroup|IEditorShape): boolean {
            return zone.getZoom() > ((zone.canvas as unknown as ICustomizerEditorCanvas).errorConfiguration ? (zone.canvas as unknown as ICustomizerEditorCanvas).errorConfiguration.zoomError : 100);
        }

        // noinspection JSUnusedGlobalSymbols - used in customizer.ts
        public getPreviewImageSize(): number {
            return this.previewImageSize;
        }

        // Private Functions
        private enableWheel(): void {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return;
            }
            this.fabricCanvas.wrapperEl.addEventListener('wheel', (e: WheelEvent): void => {
                if (this.currentZone instanceof SF.Editor.PhotoGroup) {
                    this.handleMouseWheel(e);
                }
            }, {passive: true});
        }

        private handleMouseWheel(e: WheelEvent): void {
            const fabricCanvas = this.fabricCanvas;
            if (!fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return;
            }

            const zoom = (this.currentZone as IEditorPhotoGroup).getZoom();
            const photoObject: IEditorEditorPhoto = (this.currentZone as IEditorPhotoGroup).photoObject;
            let zoomDelta = e.deltaY / (photoObject.image.width * -1);
            const newZoom = zoom + zoomDelta;
            const minZoom = photoObject.getMinZoom();
            const maxZoom = ((this.currentZone as IEditorPhotoGroup).canvas as unknown as ICustomizerEditorCanvas).errorConfiguration ?
                ((this.currentZone as IEditorPhotoGroup).canvas as unknown as ICustomizerEditorCanvas).errorConfiguration.zoomError : 100;

            if (minZoom > newZoom || maxZoom < newZoom) {
                if (zoom === minZoom || zoom === maxZoom) {
                    return;
                }

                if (minZoom > newZoom) {
                    zoomDelta = minZoom - zoom;
                } else if (minZoom < newZoom) {
                    zoomDelta = maxZoom - zoom;
                }

            }

            const zoomBeforeChange = photoObject.imagePosition.zoom;
            (this.currentZone as IEditorPhotoGroup).zoom(zoomDelta);
            if (photoObject.imagePosition.zoom == zoomBeforeChange) {
                (photoObject.group as any).userZoom = minZoom;
            } else {
                (photoObject.group as any).userZoom = photoObject.imagePosition.zoom;
            }

            fabricCanvas.renderAll();

            // fire change events
            if (this.timeout) {
                clearTimeout(this.timeout);
            }

            this.timeout = window.setTimeout((): void => {
                fabricCanvas.fire('object:modified', {target: this.currentZone});
                if (this.currentZone) {
                    this.currentZone.fire('modified');
                }
            }, 500);
        }

        private getTarget<T>(event: fabric.IEvent): T | undefined {
            return (event.selected && event.selected.length && event.selected[0] ? event.selected[0] : event.target) as T;
        }

        private checkObjectPosition(event: fabric.IEvent): void {
            this.checkCenter(event.target as fabric.Object);
        }

        private checkCenter(target: fabric.Object): void {
            if (!this.fabricCanvas) {
                console.error('No fabric canvas');
                return;
            }
            const zoom = this.fabricCanvas.getZoom();
            const centerPoint = target.getCenterPoint();

            const pageHeight = this.getVisiblePagesHeight();
            const pageWidth = this.getVisiblePagesWidth();

            const canvasHeight = pageHeight * zoom;
            const canvasWidth = pageWidth * zoom;

            let edge: string = '';
            let value: number = 0;

            // Check if center X is outside canvas bounds (allowing 50% overflow)
            if (centerPoint.x < 0) {
                edge = 'left';
                value = 0;
                this.keepObjectOnCanvasArea(edge, value, target);
            } else if (centerPoint.x > canvasWidth / zoom) {
                edge = 'right';
                value = canvasWidth / zoom;
                this.keepObjectOnCanvasArea(edge, value, target);
            }

            // Check if center Y is outside canvas bounds (allowing 50% overflow)
            if (centerPoint.y < 0) {
                edge = 'top';
                value = 0;
                this.keepObjectOnCanvasArea(edge, value, target);
            } else if (centerPoint.y > canvasHeight / zoom) {
                edge = 'bottom';
                value = canvasHeight / zoom;
                this.keepObjectOnCanvasArea(edge, value, target);
            }
        }

        private keepObjectOnCanvasArea(edge: string, value: number, target: fabric.Object): void {
            target.set(edge === 'bottom' || edge === 'top' ? 'top' : 'left', value);
            target.setCoords();
        }

        private enableGhosting(event: fabric.IEvent): void {
            if (!this.fabricCanvas) {
                console.error('No fabric canvas');
                return;
            }
            // Enable ghosting
            // we only care if there is a current transformation
            const canvas = this.fabricCanvas;

            if (!canvas._currentTransform || !canvas._currentTransform.action) {
                return;
            }

            const action = canvas._currentTransform.action;

            if (action == 'pan' || action == 'innerRotate' || action == 'drag') {
                const t: any = canvas._currentTransform.target as any;

                // Ghosting only works by bringing the image forward
                let currentStackIndex: number;
                if (action === 'pan' || action === 'drag') {
                    const target = this.getTarget<IEditorShape>(event);
                    if (target) {
                        currentStackIndex = this.fabricCanvas.getObjects().indexOf(target);
                    } else {
                        console.warn('target is not valid');
                    }
                }

                // enable ghosting
                if (t instanceof SF.Editor.PhotoGroup && !(t as IEditorPhotoGroup).isGhosted()) {
                    (t as IEditorPhotoGroup).ghost();

                    // re-render right away as mouse down doesn't render until move
                    canvas.renderAll();
                }

                const mouseUpEvent = (data: IEvent) => {
                    if (data.target instanceof SF.Editor.PhotoGroup) {
                        (data.target as IEditorPhotoGroup).unGhost();
                    }

                    if (action === 'pan' || action === 'drag') {
                        data.target.moveTo(currentStackIndex);
                        delete data.target.currentStackIndex;
                        data.target.canvas.fire('object:layer:changed', {target: data.target});

                    }
                    if (action === 'pan' || action === 'innerRotate' || action === 'drag') {
                        data.target.fire('modified');
                        if (this._onZoneModified && data.target.uid) {
                            const selectElement = this.getElementFromZone(data.target);
                            if (selectElement && this.currentZone) {
                                const currentZoom = typeof data.target.getZoom === 'function' ? data.target.getZoom() : data.target.userZoom;
                                this._onZoneModified(selectElement.freeObjectId as number, {
                                    type: 'image',
                                    zoom: currentZoom,
                                    zone: this.currentZone
                                });
                            } else {
                                console.warn('No select element found for zone, or this.currentZone is null', data.target, this.currentZone);
                            }
                        }
                    }

                    // do it in next tick so the fabric.js event fire-r doesn't get confused
                    setTimeout(function (): void {
                        canvas.off('mouse:up', mouseUpEvent);
                    }, 0);

                    // re-render
                    canvas.renderAll();
                };

                // listen for when to end ghosting
                canvas.on('mouse:up', mouseUpEvent);
            }
        }

        private async setupControls(): Promise<void> {
            const ROTATE_HANDLE_WIDTH = 20;
            const INNER_ROTATION = 'ir';
            const GRAB_HANDLE_WIDTH = 20;

            const imagePromises: Promise<void>[] = [];

            //  Get the panIcon, etc...
            const panIcon: HTMLImageElement = fabric.util.createImage();
            imagePromises.push(new Promise<void>(resolve => {
                panIcon.onload = () => resolve();
                panIcon.src = this.options.panIcon;
            }));

            const resolutionWarning: HTMLImageElement = fabric.util.createImage();
            imagePromises.push(new Promise<void>(resolve => {
                resolutionWarning.onload = () => resolve();
                resolutionWarning.src = this.options.resolutionWarningIcon;
            }));
            const resolutionError: HTMLImageElement = fabric.util.createImage();
            imagePromises.push(new Promise<void>(resolve => {
                resolutionError.onload = () => resolve();
                resolutionError.src = this.options.resolutionErrorIcon;
            }));

            let grabHandleRotate: HTMLImageElement;
            if (this.options.drawInnerRotate) {
                imagePromises.push(new Promise<void>(resolve => {
                    grabHandleRotate = fabric.util.createImage();
                    grabHandleRotate.onload = () => resolve();
                    grabHandleRotate.src = SF.staticUrlRoot + 'images/editor/icons/svg/grab_handle_rotate.svg';
                }));
            }

            let grabHandle: HTMLImageElement;
            if (this.options.drawGrabHandle) {
                imagePromises.push(new Promise<void>(resolve => {
                    grabHandle = fabric.util.createImage();
                    grabHandle.onload = onload = () => resolve();
                    grabHandle.src = SF.staticUrlRoot + 'images/editor/icons/svg/grab_handle.svg';
                }));
            }

            const options = this.options;

            if (!options.drawBorders) {
                fabric.Object.prototype.drawBorders = function (ctx: CanvasRenderingContext2D): any {
                    // Mobile doesn't do groups, and doesn't draw borders around backgrounds or photos
                    // tslint:disable:no-invalid-this
                    if (!this.hasBorders || this.group) {
                        return this;
                    }
                    let width: number;
                    let height: number;
                    if (!(this instanceof SF.Editor.Background)) {
                        const padding2 = this.padding * 2;
                        const vpt = this.getViewportTransform();
                        ctx.save();
                        ctx.globalAlpha = (this as any).isMoving ? this.borderOpacityWhenMoving || 0 : 1;

                        let selectionMultiplier = 1;

                        if (this.type == 'sf-text') {
                            this.borderColor = options.textBorderColor || this.fill as string;
                            ctx.lineWidth = options.textBorderWidth || 0;
                            selectionMultiplier = Math.floor((options.textBorderWidth || 0) / 2);
                            this.strokeWidth = options.textBorderWidth || this.strokeWidth;
                        }
                        ctx.strokeStyle = this.borderColor || '';

                        // _constrainScale is not exposed, so we have to cast as any
                        const scaleX = 1 / (this as any)._constrainScale(this.scaleX);
                        const scaleY = 1 / (this as any)._constrainScale(this.scaleY);

                        // We are a circle or speechBubble then we don't use scaled width or height...
                        if ((this as any).mainType && (this as any).mainType == 'shapeAsset' &&
                            this.type !== ShapeAssetTypes.Rectangle
                            && this.type !== ShapeAssetTypes.Triangle) {
                            width = this.width || 0;
                            height = this.height || 0;
                        } else {
                            width = this.getScaledWidth();
                            height = this.getScaledHeight();
                        }

                        if (this.type === 'sf-text' && options.textBorderWidth) {
                            // TODO: Do something with text???
                        }

                        const strokeWidth = this.strokeWidth > 1 ? this.strokeWidth : 0;
                        width += strokeWidth / scaleX;
                        height += strokeWidth / scaleY;

                        const transformedWidthHeight = fabric.util.transformPoint(new fabric.Point(width, height), vpt, true);

                        const transformedWidth = transformedWidthHeight.x;
                        const transformedHeight = transformedWidthHeight.y;

                        // code to draw dashed array stolen from drawSelection
                        ctx.beginPath();
                        const rectX = Math.floor(-(transformedWidth / 2) - this.padding) - 0.5;
                        const rectY = Math.floor(-(transformedHeight / 2) - this.padding) - 0.5;
                        const rectWidth = Math.floor(transformedWidth + padding2) + 1;
                        const rectHeight = Math.floor(transformedHeight + padding2) + 1;

                        ctx.setLineDash([3 * selectionMultiplier, 3]);
                        ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
                        ctx.closePath();
                        (ctx as any).stroke();   // Are we sure this good, ctx.stroke is a string?
                        ctx.restore();
                    }
                    ctx.save();
                    width = this.width * this.scaleX;
                    height = this.height * this.scaleY;
                    const viewportTransform = this.getViewportTransform();
                    const widthHeight = fabric.util.transformPoint(new fabric.Point(width, height), viewportTransform, true);

                    const rect = {
                        width: widthHeight.x,
                        height: widthHeight.y,
                        left: -(widthHeight.x / 2),
                        top: -(widthHeight.y / 2)
                    };
                    if (options.drawHandIcon) {
                        drawHandIcon(ctx, rect, panIcon);
                    }
                    if (this.selectable && (this as IEditorPhotoGroup).photoObject && (this as IEditorPhotoGroup).photoObject.image) {
                        drawResolutionAlerts(ctx, rect, (this as IEditorPhotoGroup));
                    }
                    ctx.restore();
                    return this;

                    // tslint:enable:no-invalid-this
                };
            }

            /* tslint:disable:no-invalid-this */
            fabric.Object.prototype.drawControls = function (ctx: CanvasRenderingContext2D): any {
                if (this.pattern) {
                    return;
                }
                if ((this.group && this.group.type == 'group' && (this.canvas as any).getObjects().indexOf((this.group as any)._findGroupByUid(this.canvas)) >= 0) ||
                    // If we don't have an image yet, don't show the controls...
                    ((this as IEditorPhotoGroup).photoObject && !(this as IEditorPhotoGroup).photoObject.image)) {
                    return;
                }

                const viewportTransform = this.getViewportTransform();
                const canvas = this.canvas;

                ctx.save();

                const width = this.width * this.scaleX;
                const height = this.height * this.scaleY;

                const widthHeight = fabric.util.transformPoint(new fabric.Point(width, height), viewportTransform, true);

                const rect = {
                    width: widthHeight.x,
                    height: widthHeight.y,
                    left: -(widthHeight.x / 2),
                    top: -(widthHeight.y / 2)
                };

                // resolution warning
                const state = (canvas as any)?.state || {};

                // Draw Resolution warnings and setup tooltips...
                if (state && state.showResolutionWarnings && this.selectable && (this as IEditorPhotoGroup).photoObject &&
                    (this as IEditorPhotoGroup).photoObject.image) {
                    drawResolutionAlerts(ctx, rect, (this as IEditorPhotoGroup));
                }

                if (this.hasControls) {

                    const halfGrabHandleWidth = GRAB_HANDLE_WIDTH / 2;

                    if (this.hasControls && options.drawGrabHandle && this.type !== 'sf-text') {

                        // top-left
                        ctx.drawImage(grabHandle,
                            rect.left - halfGrabHandleWidth,
                            rect.top - halfGrabHandleWidth, GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH);

                        // top-right
                        ctx.drawImage(grabHandle,
                            rect.left + rect.width - halfGrabHandleWidth,
                            rect.top - halfGrabHandleWidth, GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH);

                        // bottom-left
                        ctx.drawImage(grabHandle,
                            rect.left - halfGrabHandleWidth,
                            rect.top + rect.height + halfGrabHandleWidth - GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH);

                        // bottom-right
                        ctx.drawImage(grabHandle,
                            rect.left + rect.width + halfGrabHandleWidth - GRAB_HANDLE_WIDTH,
                            rect.top + rect.height + halfGrabHandleWidth - GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH);
                    }

                    if (this.type == 'sf-text' && options.drawGrabHandle) {
                        if (!this.get('lockUniScaling')) {
                            // middle-right
                            ctx.drawImage(grabHandle,
                                rect.left + rect.width - halfGrabHandleWidth + this.strokeWidth,
                                rect.top + (rect.height / 2) - halfGrabHandleWidth, GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH);

                            // middle-left
                            ctx.drawImage(grabHandle,
                                rect.left - halfGrabHandleWidth - this.strokeWidth - 2,
                                rect.top + rect.height / 2 - halfGrabHandleWidth, GRAB_HANDLE_WIDTH, GRAB_HANDLE_WIDTH);
                        }
                    }
                }

                // draw inner rotate
                if (this.hasControls && options.drawInnerRotate
                    && this.isControlVisible(INNER_ROTATION)
                    && !this.get('lockRotation')
                    && (this.type !== 'sf-text' || options.textHasInnerRotate)) {
                    ctx.drawImage(grabHandleRotate, rect.left + (rect.width / 2) - (ROTATE_HANDLE_WIDTH / 2),
                        rect.top - (this as any).innerRotateOffset - (ROTATE_HANDLE_WIDTH / 2),
                        ROTATE_HANDLE_WIDTH,
                        ROTATE_HANDLE_WIDTH);
                }

                // Draw Pan Control...
                if (this.hasControls && this.isControlVisible('pan') && options.drawHandIcon) {
                    // draw pan object
                    drawHandIcon(ctx, rect, panIcon);
                }

                ctx.restore();
                return this;
            };

            function drawHandIcon(ctx: CanvasRenderingContext2D, rect: {
                width: number,
                height: number,
                left: number,
                top: number
            }, panIconElement: HTMLImageElement): void {
                ctx.save();
                const panIconHeight = panIconElement.height;
                const panIconWidth = panIconElement.width;
                // circle
                ctx.beginPath();
                ctx.arc(rect.left + rect.width / 2, rect.top + rect.height / 2, 20, 0, 2 * Math.PI, false);
                ctx.fillStyle = 'rgba(185, 185, 185, 0.5)';
                (ctx as any).fill(); // TODO: XXX: Check, fill is a string or pattern... Are we sure this is valid?

                // open hand
                ctx.drawImage(
                    panIconElement,
                    rect.left + rect.width / 2 - panIconWidth / 2,
                    rect.top + rect.height / 2 - panIconHeight / 2,
                    panIconWidth,
                    panIconHeight
                );
                ctx.restore();

            }

            function drawResolutionAlerts(ctx: CanvasRenderingContext2D, rect: {
                width: number,
                height: number,
                left: number,
                top: number
            }, selectedObject: IEditorPhotoGroup): void {
                const canvas = selectedObject.canvas;
                const offsetX = 0;
                const offsetY = 0;
                // const theta = fabric.util.degreesToRadians(45 - this.angle);

                if (selectedObject.isImageTooLarge() || selectedObject.getZoom() >= (canvas as unknown as ICustomizerEditorCanvas).errorConfiguration.zoomError) {
                    ctx.drawImage(
                        resolutionError,
                        rect.left + offsetX,
                        rect.top + rect.height - resolutionError.height - offsetY,
                        resolutionError.width,
                        resolutionError.height
                    );

                    const warningText = SF.tr('WARNING_ERROR_IMAGE_QUALITY', {}, 'Image Quality Error', false);
                    const warningLeftInitial = rect.left + resolutionError.width - 5;
                    const warningTopInitial = rect.top + rect.height;

                    selectedObject.drawWarningErrorContainer(ctx, warningText, warningTopInitial, warningLeftInitial,
                        '#e01717', '#ffffff', resolutionError.width, 20);

                } else if (selectedObject.isImageWarning() || selectedObject.getZoom() >= (canvas as unknown as ICustomizerEditorCanvas).errorConfiguration.zoomWarning) {
                    ctx.drawImage(
                        resolutionWarning,
                        rect.left + offsetX,
                        rect.top + rect.height - resolutionWarning.height - offsetY,
                        resolutionWarning.width,
                        resolutionWarning.height
                    );

                    const warningText = SF.tr('WARNING_POOR_IMAGE_QUALITY', {}, 'Poor Image Quality', false);
                    const warningLeftInitial = rect.left + resolutionError.width - 5;
                    const warningTopInitial = rect.top + rect.height;

                    selectedObject.drawWarningErrorContainer(ctx, warningText, warningTopInitial, warningLeftInitial,
                        '#feb303', '#ffffff', resolutionWarning.width, 20);

                }
            }

            await Promise.all(imagePromises);
        }

        private async getPreviewImage(outputFormat: string): Promise<string> {
            const canvasElement = await this.toCanvasElement(outputFormat);
            this.resetCanvasThreeDPreview();
            return new Promise((resolve: (base64Image: string) => void, reject: (err: string) => void): void => {
                const crop = ImagingUtils.getScaledCrop(this.getVisiblePagesWidth(), this.getVisiblePagesHeight(), this.previewImageSize, this.previewImageSize);
                if (canvasElement) {
                    const res = ImagingUtils.drawImageWithCrop(canvasElement, crop, {
                        x: 0,
                        y: 0,
                        width: canvasElement.width,
                        height: canvasElement.height
                    }, 85, outputFormat);
                    if (res) {

                        resolve(outputFormat === 'png' ? res : res.split(/,/, 2)[1]);
                    } else {
                        reject('Unable to get context');
                    }
                } else {
                    reject('Unable to get canvas element');
                }
            });
        }

        private getBase64ImageFromDataUrl(dataUrl: string): string {
            const splitPosition = dataUrl.indexOf(',');
            if (splitPosition >= 0) {
                return dataUrl.substring(splitPosition + 1);
            } else {
                return '';
            }
        }

        private elementHasResolutionWarning(element: IEditorPhotoElement): ElementResolutionError {
            if (!this.fabricCanvas) {
                console.error('Fabric canvas is not setup');
                return ElementResolutionError.Error;
            }
            const zoom = element.imagePosition.zoom;
            if (zoom < this.fabricCanvas.errorConfiguration.zoomWarning) {
                return ElementResolutionError.None;
            } else if (zoom < this.fabricCanvas.errorConfiguration.zoomError) {
                return ElementResolutionError.Warning;
            }
            return ElementResolutionError.Error;
        }

        private isAnUneditedText(element: IEditorTextElement): boolean {
            const adjustedTextSize = (element as any).adjustedTextSize;
            return (!element.edited && (adjustedTextSize && element.text == (DefaultText as any)[adjustedTextSize] ||
                (element.placeHolderTranslations && element.text == element.placeHolderTranslations[SF.currentLanguage]) ||
                Object.keys(DefaultText).some(size => {
                    return (DefaultText as any)[size] === element.text;
                })));
        }

        private preloadFonts(): void {
            const fonts: { [key: string]: string[] } = {};

            this.visiblePages.forEach((page: IEditorPage): void => {
                page.elements
                    .filter((element: IEditorElement): boolean => {
                        return element instanceof SF.Model.TextElement;
                    })
                    .forEach((element: IEditorElement): void => {
                        // find font used in rootStyle
                        const textElement: IEditorTextElement = element as unknown as IEditorTextElement;
                        this.checkAndAdd(textElement.rootStyle, fonts);

                        // find all fonts used in styles
                        if (textElement.styles) {
                            Object.keys(textElement.styles).map((style: string): void => {
                                if (textElement.styles[style]) {
                                    Object.keys(textElement.styles[style]).forEach((char: any): void => {
                                        this.checkAndAdd(char, fonts);
                                    });
                                }
                            });
                        }
                    });
            });

            this.fontService.preLoad(fonts);
        }

        private checkAndAdd(stylesObject: any, fontFamilyDescriptions: { [key: string]: string[] }): void {
            fontFamilyDescriptions = fontFamilyDescriptions || {};
            let fontFamily = stylesObject.fontFamily;

            // if a font family declaration exists
            if (fontFamily) {
                // ensure the font with the style exists
                let style = (SF.Util as any).Fonts.getStyleFromStylesObject(stylesObject);
                const font = this.fontService.get(fontFamily);

                // ensure that font, and the style exists, otherwise, we will map it
                if (!font || font.styles.indexOf(style) === -1) {
                    if (fontFamily === 'Impact') {
                        fontFamily = 'Roboto';
                        style = 'r';
                    } else {
                        fontFamily = 'Lato';
                    }

                    stylesObject.fontFamily = fontFamily;
                    stylesObject.fontWeight = style === 'b' || style == 'bi' ? 'bold' : 'normal';
                    stylesObject.fontStyle = style === 'i' || style == 'bi' ? 'italic' : 'normal';
                }

                // add it to list if it doesn't exist
                if (!fontFamilyDescriptions.hasOwnProperty(fontFamily)) {
                    fontFamilyDescriptions[fontFamily] = [];
                }

                // add it as one of the styles if it doesn't already exist
                if (fontFamilyDescriptions[fontFamily].indexOf(style) === -1) {
                    fontFamilyDescriptions[fontFamily].push(style);
                }
            }
        }

    }

